import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AddSideEvent from './AddSideEvent';
import AddBowlerModal from '../bookie/AddBowlerModal';
import BookiePayoutModal from '../bookie/BookiePayoutModal';
import SideEventPayoutPresets from '../SideEventPayoutPresets';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (d: string) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TYPE_LABELS: Record<string, string> = {
  high_game: 'High Game',
  high_series: 'High Series',
  mystery_score: 'Mystery Score',
  mystery_series: 'Mystery Series',
  clean_game: 'Clean Game',
  survivor: 'Survivor',
  eliminator: 'Eliminator',
  side_pots: 'Side Pots',
  bracket: 'Bracket',
  custom: 'All with All',
};

const TYPE_COLORS: Record<string, string> = {
  high_game: 'bg-yellow-900/40 text-yellow-300',
  high_series: 'bg-orange-900/40 text-orange-300',
  mystery_score: 'bg-pink-900/40 text-pink-300',
  mystery_series: 'bg-rose-900/40 text-rose-300',
  clean_game: 'bg-green-900/40 text-green-300',
  survivor: 'bg-red-900/40 text-red-300',
  eliminator: 'bg-purple-900/40 text-purple-300',
  side_pots: 'bg-blue-900/40 text-blue-300',
  bracket: 'bg-indigo-900/40 text-indigo-300',
  custom: 'bg-amber-900/40 text-amber-300',
};

interface Squad {
  id: number;
  name: string;
  date: string;
  start_time: string;
  end_time: string;
  games_per_squad?: number;
}

interface AdminSideEvent {
  id: number;
  name: string;
  description: string;
  type: string;
  applies_to: string;
  cost: number;
  bracket_size?: number;
  squad_id: number;
  squad_name: string;
  squad_date: string;
  league_name: string;
  entry_count: number;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  hdcp?: boolean;
}

interface Entry { id?: number; bowler_id: number; bowler_name: string; paid: boolean; }
interface BracketEntry { bowler_id: number; name: string; entryCount: number; }
interface Bowler { id: number; name: string; email?: string; gender?: string | null; dob?: string | null; }
interface BracketMatchupData {
  id: number; matchup_number: number;
  entry1_id: number | null; entry1_name: string | null; entry1_bowler_id: number | null; entry1_score: number | null;
  entry2_id: number | null; entry2_name: string | null; entry2_bowler_id: number | null; entry2_score: number | null;
  winner_entry_id: number | null; winner_name: string | null; is_bye: boolean;
}
interface BracketRoundData { id: number; round_number: number; game_number: number; matchups: BracketMatchupData[]; }
interface BracketData { id: number; bracket_number: number; rounds: BracketRoundData[]; }
interface Financial { name: string; type: string; squad_name?: string; squad_date?: string; total_entries: number; cost: number; total_pot: number; }
interface Payout { id: number; recipient_name: string | null; recipient_id: number; se_name: string; se_type: string; squad_name?: string; squad_date?: string; amount: number; payout_date?: string; notes?: string; checkmark?: number; }

interface Props {
  leagueId: number;
  onBack: () => void;
  onNavigateToSchedule?: () => void;
}

/* ─── Print helpers (no API calls - pure HTML generation) ──────────────── */
const PAID_BADGE = '<span style="display:inline-block;background:#fef3c7;color:#92400e;font-size:9px;font-weight:600;padding:1px 5px;border-radius:3px;margin-left:5px;">✓ PAID</span>';

function buildWinnersHTML(seResult: any, paidIds?: Set<number>): string {
  const paidBadge = (id: number) => paidIds?.has(id) ? PAID_BADGE : '';
  const type = seResult.type;
  if (type === 'survivor') {
    const rounds: any[] = seResult.computed_rounds || [];
    if (!rounds.length) return '<em style="color:#9ca3af;">No rounds data yet</em>';
    const rows = rounds.map((r: any) => `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Min: <strong>${r.survivor_score}</strong></td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${(r.survivor_list||[]).map((b:any)=>`${b.bowler_name}(${b.score})`).join(', ')||'—'}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${(r.eliminated_list||[]).map((b:any)=>`${b.bowler_name}(${b.score})`).join(', ')||'—'}</td>
    </tr>`).join('');
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Game</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Threshold</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#15803d;">Survived</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#b91c1c;">Eliminated</th>
    </tr></thead><tbody>${rows}</tbody></table>
    <div style="margin-top:10px;padding:8px 12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;font-size:12px;">
      <strong>Finalists:</strong> ${(seResult.cashers||[]).map((c:any)=>`${c.bowler_name}${paidBadge(c.bowler_id)}`).join(', ')||'None yet'}
    </div>`;
  }
  if (type === 'eliminator') {
    const rounds: any[] = seResult.computed_rounds || [];
    if (!rounds.length) return '<em style="color:#9ca3af;">No rounds data yet</em>';
    const rows = rounds.map((r: any) => `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Advance: <strong>${r.advancement_percentage}%</strong></td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${(r.advancer_list||[]).map((b:any)=>`${b.bowler_name}(${b.score})`).join(', ')||'—'}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${(r.eliminated_list||[]).map((b:any)=>`${b.bowler_name}(${b.score})`).join(', ')||'—'}</td>
    </tr>`).join('');
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Game</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Threshold</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#15803d;">Advancing</th>
      <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#b91c1c;">Eliminated</th>
    </tr></thead><tbody>${rows}</tbody></table>
    <div style="margin-top:10px;padding:8px 12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;font-size:12px;">
      <strong>Finalists:</strong> ${(seResult.cashers||[]).map((c:any)=>`${c.bowler_name}${paidBadge(c.bowler_id)}`).join(', ')||'None yet'}
    </div>`;
  }
  if (type === 'side_pots' || type === 'custom') {
    const pots: any[] = seResult.pot_results || [];
    if (!pots.length) return '<em style="color:#9ca3af;">No scores entered</em>';
    return pots.map((pot: any) => {
      const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
      const all: any[] = pot.all_entries || pot.winners || [];
      const rows = all.map((b: any, i: number) => {
        const isPaid = pot.is_doubles
          ? (b.bowler_ids || []).some((bi: any) => paidIds?.has(bi.bowler_id))
          : paidIds?.has(b.bowler_id);
        const paid = isPaid ? PAID_BADGE : '';
        return `<div style="padding:2px 0;${i<pot.winner_count?'color:#15803d;font-weight:600;':''}">${i+1}. ${b.bowler_name}${paid} — ${b.score}${i<pot.winner_count?' ✓':''}</div>`;
      }).join('');
      return `<div style="margin-bottom:10px;"><strong style="font-size:11px;color:#6b7280;">${label}</strong><div style="margin-top:3px;font-size:12px;">${rows||'<em style="color:#9ca3af;">No scores</em>'}</div></div>`;
    }).join('');
  }
  const standings: any[] = seResult.standings || [];
  if (!standings.length) return '<em style="color:#9ca3af;">No scores entered</em>';
  return standings.map((s: any, i: number) =>
    `<div style="padding:2px 0;">${i+1}. ${s.bowler_name}${paidBadge(s.bowler_id)} — ${type==='high_series'?`${s.total_score} (series)`:`${s.best_game}`}</div>`
  ).join('');
}

const SKIP_PRINT_TYPES = new Set(['mystery_score', 'mystery_series', 'clean_game', 'bracket', 'custom']);

function printWinners(leagueName: string, scopeLabel: string, seResults: any[], paidMap?: Map<number, Set<number>>) {
  const printable = seResults.filter(se => !SKIP_PRINT_TYPES.has(se.type));
  if (!printable.length) { alert('No printable side event data.'); return; }
  const sections = printable.map(se => `<div style="margin-bottom:24px;page-break-inside:avoid;">
    <h3 style="margin:0 0 6px;font-size:14px;">${se.name} <span style="color:#6b7280;font-weight:400;font-size:12px;">(${TYPE_LABELS[se.type]||se.type})</span></h3>
    <div style="font-size:13px;line-height:1.7;">${buildWinnersHTML(se, paidMap?.get(se.id))}</div>
  </div>`).join('<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;"/>');
  const html = `<!DOCTYPE html><html><head><title>Results - ${leagueName}</title><style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{text-align:left;}</style></head><body>
    <h2 style="margin-bottom:2px;">${leagueName}</h2>
    <p style="color:#374151;font-size:14px;font-weight:600;margin:0 0 4px;">Results</p>
    <p style="color:#6b7280;font-size:13px;margin-bottom:20px;">${scopeLabel}</p>
    ${sections}
    <p style="color:#9ca3af;font-size:11px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=820,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printPayments(leagueName: string, scopeLabel: string, financials: Financial[]) {
  const allSquads = scopeLabel === 'All Squads';
  const sqHdr = allSquads ? '<th style="padding:8px 12px;background:#f3f4f6;text-align:left;">Squad</th>' : '';
  const rows = financials.map(f => `<tr>
    <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${f.name}</td>
    <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${TYPE_LABELS[f.type]||f.type}</td>
    ${allSquads?`<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${f.squad_name||''} ${f.squad_date?`(${fmtDate(f.squad_date)})`:''}</td>`:''}
    <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">${f.total_entries}</td>
    <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">$${Number(f.cost).toFixed(2)}</td>
    <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:bold;">$${Number(f.total_pot).toFixed(2)}</td>
  </tr>`).join('');
  const total = financials.reduce((s,f)=>s+Number(f.total_pot),0);
  const cols = allSquads ? 6 : 5;
  const html = `<!DOCTYPE html><html><head><title>Payments - ${leagueName}</title><style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:8px 12px;text-align:left;border-bottom:2px solid #d1d5db;}</style></head><body>
    <h2 style="margin-bottom:2px;">${leagueName}</h2>
    <p style="color:#374151;font-size:14px;font-weight:600;margin:0 0 4px;">Payments by Bowlers</p>
    <p style="color:#6b7280;margin-bottom:20px;">Scope: ${scopeLabel}</p>
    <table><thead><tr><th>Side Event</th><th>Type</th>${sqHdr}<th style="text-align:center;">Entries</th><th style="text-align:right;">Cost</th><th style="text-align:right;">Total Pot</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="${cols-1}" style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">Total</td><td style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">$${total.toFixed(2)}</td></tr></tfoot></table>
    <p style="color:#9ca3af;font-size:12px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=820,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printPayouts(leagueName: string, scopeLabel: string, payouts: Payout[]) {
  const allSquads = scopeLabel === 'All Squads';
  const sqHdr = allSquads ? '<th style="padding:8px 12px;background:#f3f4f6;text-align:left;">Squad</th>' : '';
  // Group by recipient
  const groupMap = new Map<string, Payout[]>();
  for (const p of payouts) {
    const key = `${p.recipient_id}|${p.recipient_name||''}`;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(p);
  }
  const groups = Array.from(groupMap.values())
    .map(rows => ({
      name: rows[0].recipient_name || `#${rows[0].recipient_id}`,
      rows: [...rows.filter(r => r.se_type === 'bracket'), ...rows.filter(r => r.se_type !== 'bracket')],
      total: rows.reduce((s, r) => s + Number(r.amount), 0),
    }))
    .sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));
  const rows = groups.map(g => {
    const multi = g.rows.length > 1;
    const dataRows = g.rows.map((p, ri) => `<tr style="background:${multi?'#eef2ff':'white'};border-top:${ri===0?'2px solid #9ca3af':'1px solid #e5e7eb'}">
      <td style="padding:8px 12px;font-weight:600">${ri===0?_fmtName(g.name):'<span style="color:#9ca3af;padding-left:10px">└─</span>'}</td>
      <td style="padding:8px 12px;">${p.se_name}</td>
      <td style="padding:8px 12px;">${TYPE_LABELS[p.se_type]||p.se_type}</td>
      ${allSquads?`<td style="padding:8px 12px;">${p.squad_name||''} ${p.squad_date?`(${fmtDate(p.squad_date)})`:''}</td>`:''}
      <td style="padding:8px 12px;text-align:right;font-weight:bold;color:#b91c1c;">$${Number(p.amount).toFixed(2)}</td>
      <td style="padding:8px 12px;font-size:10px;">${p.payout_date?new Date(p.payout_date).toLocaleDateString():''}</td>
      <td style="padding:8px 12px;color:#6b7280;">${p.notes||''}</td>
    </tr>`).join('');
    const subtotal = multi ? `<tr style="border-top:1px solid #6ee7b7;background:#ecfdf5;"><td colspan="${allSquads?6:5}" style="padding:5px 12px;text-align:right;font-size:10px;font-weight:600;color:#065f46;">Subtotal — ${_fmtName(g.name)}</td><td style="padding:5px 12px;text-align:right;font-weight:bold;color:#059669;">$${g.total.toFixed(2)}</td><td></td></tr>` : '';
    return dataRows + subtotal;
  }).join('');
  const total = payouts.reduce((s,p)=>s+Number(p.amount),0);
  const cols = allSquads ? 7 : 6;
  const html = `<!DOCTYPE html><html><head><title>Payouts - ${leagueName}</title><style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:8px 12px;text-align:left;border-bottom:2px solid #d1d5db;}</style></head><body>
    <h2 style="margin-bottom:2px;">${leagueName}</h2>
    <p style="color:#374151;font-size:14px;font-weight:600;margin:0 0 4px;">Payouts to Bowlers</p>
    <p style="color:#6b7280;margin-bottom:20px;">Scope: ${scopeLabel}</p>
    <table><thead><tr><th>Recipient</th><th>Side Event</th><th>Type</th>${sqHdr}<th style="text-align:right;">Amount</th><th>Date</th><th>Notes</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="${cols-1}" style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">Total Paid Out</td><td style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">$${total.toFixed(2)}</td></tr></tfoot></table>
    <p style="color:#9ca3af;font-size:12px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=900,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

const _lastName = (n: string) => n.trim().split(/\s+/).pop()?.toLowerCase() ?? '';
const _fmtName = (n: string) => { const p = n.trim().split(/\s+/); if (p.length < 2) return n; const last = p.pop()!; return `${last}, ${p.join(' ')}`; };
const _norm = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const _tok = (t: string, tgt: string): number => { if (!t || !tgt) return 0; if (tgt === t) return 3; if (tgt.startsWith(t)) return 2; if (tgt.includes(t)) return 1; return 0; };
const SUFFIX_RE = /^(jr\.?|sr\.?|i{1,3}|iv|vi{0,3})$/i;
const _tokenBagScore = (queryToks: string[], nameToks: string[]): number => {
  if (queryToks.length === 0) return 0;
  const required = queryToks.filter(t => !SUFFIX_RE.test(t));
  const optional = queryToks.filter(t => SUFFIX_RE.test(t));
  const mustMatch = required.length > 0 ? required : optional;
  const bonusToks = required.length > 0 ? optional : [];
  let total = 0;
  for (const qt of mustMatch) {
    let best = 0;
    for (const nt of nameToks) best = Math.max(best, _tok(qt, nt));
    if (best === 0) return 0;
    total += best;
  }
  for (const qt of bonusToks) {
    let best = 0;
    for (const nt of nameToks) best = Math.max(best, _tok(qt, nt));
    total += best;
  }
  return total;
};
const _bowlerMatch = (query: string, fullName: string): number => {
  const q = _norm(query); if (!q) return 0;
  const nameToks = fullName.trim().split(/\s+/).filter(Boolean).map(_norm);
  if (q.includes(',')) {
    const segs = q.split(',').map(s => s.trim()).filter(Boolean);
    const allToks = ([] as string[]).concat(...segs.map((s: string) => s.split(/\s+/).filter(Boolean)));
    return _tokenBagScore(allToks, nameToks);
  }
  return _tokenBagScore(q.split(/\s+/).filter(Boolean), nameToks);
};

function printBrackets(leagueName: string, seName: string, brackets: BracketData[], totalRounds: number, cost: number) {
  const buildMatchupHtml = (m: BracketMatchupData | undefined): string => {
    if (!m) return '<table class="mu"><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr></table>';
    const e1Won = m.winner_entry_id === m.entry1_id;
    const e2Won = m.winner_entry_id === m.entry2_id;
    const r1 = `<tr style="background:${e1Won?'#d1fae5':'white'}"><td class="mu-name" style="font-weight:${e1Won?'700':'normal'}">${m.entry1_name||'<em class="tbd">TBD</em>'}</td><td class="mu-score" style="font-weight:${e1Won?'700':'normal'}">${m.entry1_score!=null?m.entry1_score:''}</td></tr>`;
    const r2 = `<tr style="background:${e2Won?'#d1fae5':'white'}"><td class="mu-name" style="font-weight:${e2Won?'700':'normal'}">${m.is_bye?'<em class="tbd">BYE</em>':(m.entry2_name||'<em class="tbd">TBD</em>')}</td><td class="mu-score" style="font-weight:${e2Won?'700':'normal'}">${!m.is_bye&&m.entry2_score!=null?m.entry2_score:''}</td></tr>`;
    return `<table class="mu"><tbody>${r1}${r2}</tbody></table>`;
  };
  const bracketHtml = brackets.map(b => {
    const cols = Array.from({length:totalRounds},(_,ri)=>{
      const rn=ri+1; const round=b.rounds.find(r=>r.round_number===rn);
      const slotCount=Math.pow(2,totalRounds-rn); const slotH=Math.pow(2,rn-1)*40;
      const header=rn===totalRounds?'Final':`R${rn}${round?` G${round.game_number}`:''}`;
      const cells=Array.from({length:slotCount},(_,mi)=>`<div class="slot" style="height:${slotH}px">${buildMatchupHtml(round?.matchups?.[mi])}</div>`).join('');
      return `<div class="col"><div class="col-hdr">${header}</div>${cells}</div>`;
    }).join('');
    return `<div class="bracket"><div class="b-title">Bracket #${b.bracket_number}</div><div class="b-rounds">${cols}</div></div>`;
  }).join('');
  const html = `<!DOCTYPE html><html><head><title>Brackets - ${seName}</title><style>@page{size:A4 portrait;margin:12mm}body{font-family:Arial,sans-serif;font-size:10px;color:#111;margin:0}h2{font-size:14px;margin:0 0 2px}.sub{color:#6b7280;font-size:10px;margin:0 0 10px}.page-columns{columns:2;column-gap:6mm}.bracket{break-inside:avoid;-webkit-column-break-inside:avoid;border:1px solid #d1d5db;border-radius:4px;padding:5px;margin-bottom:8px}.b-title{font-weight:700;font-size:10px;margin-bottom:4px}.b-rounds{display:flex;gap:4px}.col{flex:0 0 auto}.col-hdr{text-align:center;font-size:8px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:2px;margin-bottom:2px}.slot{display:flex;align-items:center}table.mu{width:82px;border-collapse:collapse;border:1px solid #9ca3af}table.mu td{border-bottom:1px solid #d1d5db;padding:1px 3px}table.mu tr:last-child td{border-bottom:none}.mu-name{max-width:62px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:7.5px}.mu-score{width:18px;text-align:right}.tbd{color:#9ca3af;font-size:8px}.footer{color:#9ca3af;font-size:9px;margin-top:10px}</style></head><body>
    <h2>${leagueName}</h2><p class="sub" style="font-weight:600;color:#374151;">Brackets – ${seName}</p><p class="sub">Entry: $${Number(cost).toFixed(2)} · ${brackets.length} bracket${brackets.length!==1?'s':''} · ${totalRounds} rounds</p>
    <div class="page-columns">${bracketHtml}</div><p class="footer">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=820,height=1060');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printBracketCashers(leagueName: string, seName: string, cashers: {bowler_name:string;total_amount:number;payout_notes:string}[], refunds: {bowler_name:string;refund_amount:number}[], cost: number) {
  const sorted = [...cashers].sort((a,b)=>_lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const casherRows = sorted.map((c,i)=>`<tr><td class="td">${i+1}</td><td class="td" style="font-weight:600">${_fmtName(c.bowler_name)}</td><td class="td" style="text-align:right;color:#15803d;font-weight:700">$${Number(c.total_amount).toFixed(2)}</td><td class="td n">${c.payout_notes||''}</td></tr>`).join('');
  const casherTotal = sorted.reduce((s,c)=>s+Number(c.total_amount),0);
  const sortedR = [...refunds].sort((a,b)=>_lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const refundSection = sortedR.length>0 ? `<h3 style="font-size:13px;margin:24px 0 8px;">Refunds</h3><table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:right">Refund</th></tr></thead><tbody>${sortedR.map((r,i)=>`<tr><td class="td">${i+1}</td><td class="td" style="font-weight:600">${_fmtName(r.bowler_name)}</td><td class="td" style="text-align:right;color:#b45309;font-weight:700">$${Number(r.refund_amount).toFixed(2)}</td></tr>`).join('')}</tbody></table>` : '';
  const html = `<!DOCTYPE html><html><head><title>Bracket Cashers - ${seName}</title><style>@page{size:A4 portrait;margin:15mm}body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0}h2{font-size:15px;margin:0 0 3px}.sub{color:#6b7280;font-size:11px;margin:0 0 16px}table{width:100%;border-collapse:collapse}th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;font-size:11px}.td{padding:6px 10px;border-bottom:1px solid #e5e7eb}.n{font-size:10px;color:#6b7280}.footer{color:#9ca3af;font-size:10px;margin-top:20px}</style></head><body>
    <h2>${leagueName}</h2><p class="sub" style="font-weight:600;color:#374151;">Bracket Cashers – ${seName}</p><p class="sub">Entry cost: $${Number(cost).toFixed(2)} each</p>
    ${cashers.length>0?`<table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:right">Winnings</th><th>Notes</th></tr></thead><tbody>${casherRows}</tbody><tfoot><tr><td colspan="3" style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;">Total Paid Out</td><td style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;">$${casherTotal.toFixed(2)}</td></tr></tfoot></table>`:'<p style="color:#9ca3af;font-style:italic">No cashers yet.</p>'}
    ${refundSection}<p class="footer">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=700,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printBracketEntries(leagueName: string, seName: string, entries: BracketEntry[], brackets: BracketData[]) {
  const bracketMap: Record<number, number[]> = {};
  for (const b of brackets) {
    const r1 = b.rounds.find(r => r.round_number === 1);
    if (!r1) continue;
    for (const m of r1.matchups) {
      if (m.entry1_bowler_id) { if (!bracketMap[m.entry1_bowler_id]) bracketMap[m.entry1_bowler_id]=[]; bracketMap[m.entry1_bowler_id].push(b.bracket_number); }
      if (m.entry2_bowler_id && !m.is_bye) { if (!bracketMap[m.entry2_bowler_id]) bracketMap[m.entry2_bowler_id]=[]; bracketMap[m.entry2_bowler_id].push(b.bracket_number); }
    }
  }
  const sorted = [...entries].sort((a,b)=>_lastName(a.name).localeCompare(_lastName(b.name)));
  const rows = sorted.map((e,i)=>`<tr><td class="td">${i+1}</td><td class="td bname">${_fmtName(e.name)}</td><td class="td ctr">${e.entryCount}</td><td class="td">${(bracketMap[e.bowler_id]||[]).sort((a,b)=>a-b).join(', ')||'-'}</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><title>Bracket Entries - ${seName}</title><style>@page{size:A4 portrait;margin:15mm}body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0}h2{font-size:15px;margin:0 0 3px}.sub{color:#6b7280;font-size:11px;margin:0 0 16px}table{width:100%;border-collapse:collapse}th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db}.td{padding:6px 10px;border-bottom:1px solid #e5e7eb}.bname{font-weight:600}.ctr{text-align:center}.footer{color:#9ca3af;font-size:10px;margin-top:20px}</style></head><body>
    <h2>${leagueName}</h2><p class="sub" style="font-weight:600;color:#374151;">Bracket Entries – ${seName}</p><p class="sub">${sorted.length} bowlers · sorted by last name</p>
    <table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:center">Entries</th><th>Bracket #s</th></tr></thead><tbody>${rows}</tbody></table>
    <p class="footer">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=700,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printAliveBowlers(leagueName: string, seName: string, brackets: BracketData[]) {
  const aliveMap: Record<number, {name:string;bracketNumbers:number[]}> = {};
  for (const b of brackets) {
    const r1 = b.rounds.find(r=>r.round_number===1); if (!r1) continue;
    const byEntry: Record<number,{bowlerId:number;name:string}> = {};
    for (const m of r1.matchups) {
      if (m.entry1_id&&m.entry1_bowler_id) byEntry[m.entry1_id]={bowlerId:m.entry1_bowler_id,name:m.entry1_name||''};
      if (m.entry2_id&&m.entry2_bowler_id&&!m.is_bye) byEntry[m.entry2_id]={bowlerId:m.entry2_bowler_id,name:m.entry2_name||''};
    }
    const elim=new Set<number|null>();
    for (const round of b.rounds) for (const m of round.matchups) {
      if (!m.winner_entry_id) continue;
      if (m.winner_entry_id===m.entry1_id&&m.entry2_id) elim.add(m.entry2_id);
      if (m.winner_entry_id===m.entry2_id&&m.entry1_id) elim.add(m.entry1_id);
    }
    for (const [eid,p] of Object.entries(byEntry)) {
      if (elim.has(Number(eid))) continue;
      if (!aliveMap[p.bowlerId]) aliveMap[p.bowlerId]={name:p.name,bracketNumbers:[]};
      aliveMap[p.bowlerId].bracketNumbers.push(b.bracket_number);
    }
  }
  const alive = Object.values(aliveMap).sort((a,b)=>_lastName(a.name).localeCompare(_lastName(b.name)));
  const rows = alive.map((e,i)=>`<tr><td class="td">${i+1}</td><td class="td bname">${_fmtName(e.name)}</td><td class="td">${[...e.bracketNumbers].sort((a,b)=>a-b).join(', ')}</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><title>Alive - ${seName}</title><style>@page{size:A4 portrait;margin:15mm}body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0}h2{font-size:15px;margin:0 0 3px}.sub{color:#6b7280;font-size:11px;margin:0 0 16px}table{width:100%;border-collapse:collapse}th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db}.td{padding:6px 10px;border-bottom:1px solid #e5e7eb}.bname{font-weight:600}.footer{color:#9ca3af;font-size:10px;margin-top:20px}</style></head><body>
    <h2>${leagueName}</h2><p class="sub" style="font-weight:600;color:#374151;">Alive Bowlers – ${seName}</p><p class="sub">${alive.length} still alive · sorted by last name</p>
    <table><thead><tr><th>#</th><th>Bowler</th><th>Alive in Brackets</th></tr></thead><tbody>${rows||'<tr><td colspan="3" style="padding:10px;color:#9ca3af;text-align:center">No data yet</td></tr>'}</tbody></table>
    <p class="footer">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w = window.open('', '_blank', 'width=700,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

const LIVE_TYPES = new Set(['survivor','eliminator','mystery_score','mystery_series','side_pots','high_game','high_series','custom']);

/* ─── AddSquadModal ─────────────────────────────────────────────────────── */
const AddSquadModal: React.FC<{ leagueId: number; onClose: () => void; onAdded: () => void }> = ({ leagueId, onClose, onAdded }) => {
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const handleAdd = async () => {
    if (!date) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/leagues/${leagueId}/squads`, { date }, { headers: authHeader() });
      onAdded();
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-sm border border-gray-700 p-6">
        <h2 className="text-white font-bold text-lg mb-4">Add Squad</h2>
        <label className="text-xs text-gray-400 block mb-1">Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-gray-900 text-white text-sm px-3 py-2 rounded border border-gray-700 outline-none mb-4" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button onClick={handleAdd} disabled={!date || saving} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-sm text-white rounded-lg disabled:opacity-50">
            {saving ? 'Adding…' : 'Add Squad'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── FinancialsPanel ───────────────────────────────────────────────────── */
const FinancialsPanel: React.FC<{ leagueId: number; squadId: number | null; leagueName: string; squadName?: string; onClose: () => void }> = ({ leagueId, squadId, leagueName, squadName, onClose }) => {
  const [financials, setFinancials] = useState<Financial[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const finUrl = squadId ? `${API}/admin/manage-side-events/squads/${squadId}/financials` : `${API}/admin/manage-side-events/leagues/${leagueId}/financials`;
    const payUrl = squadId ? `${API}/admin/manage-side-events/squads/${squadId}/payouts` : `${API}/admin/manage-side-events/leagues/${leagueId}/payouts`;
    try {
      const [fRes, pRes] = await Promise.all([fetch(finUrl, { headers: authHeader() }), fetch(payUrl, { headers: authHeader() })]);
      if (fRes.ok) { const d = await fRes.json(); setFinancials(Array.isArray(d) ? d : (d.sideEvents || [])); }
      if (pRes.ok) { const d = await pRes.json(); setPayouts(Array.isArray(d) ? d : (d.payouts || [])); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [leagueId, squadId]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleCheckmark = async (payoutId: number, current: number | undefined) => {
    const newVal = current ? 0 : 1;
    setPayouts(prev => prev.map(p => p.id === payoutId ? { ...p, checkmark: newVal } : p));
    try {
      await axios.put(`${API}/admin/payouts/${payoutId}/checkmark`, { checkmark: newVal }, { headers: authHeader() });
    } catch { setPayouts(prev => prev.map(p => p.id === payoutId ? { ...p, checkmark: current } : p)); }
  };
  const groupedPayouts = (() => {
    const map = new Map<string, Payout[]>();
    for (const p of payouts) {
      const key = `${p.recipient_id}|${p.recipient_name || ''}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return Array.from(map.values()).map(rows => ({
      name: rows[0].recipient_name || `#${rows[0].recipient_id}`,
      rows: [...rows.filter(r => r.se_type === 'bracket'), ...rows.filter(r => r.se_type !== 'bracket')],
      total: rows.reduce((s, r) => s + Number(r.amount), 0),
    })).sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));
  })();
  const totalPot = financials.reduce((s, f) => s + Number(f.total_pot), 0);
  const totalOut = payouts.reduce((s, p) => s + Number(p.amount), 0);
  const scopeLabel = squadId ? (squadName || 'This Squad') : 'All Squads';
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-white font-bold text-lg">Financials — {leagueName}</h2>
            <p className="text-gray-400 text-xs mt-0.5">Scope: {scopeLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => printPayments(leagueName, scopeLabel, financials)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded-lg">💳 Print Payments</button>
            <button onClick={() => printPayouts(leagueName, scopeLabel, payouts)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded-lg">💸 Print Payouts</button>
            <button onClick={onClose} className="text-gray-400 hover:text-white ml-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        {loading ? <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Loading…</div> : (
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            <div>
              <h3 className="text-gray-300 font-semibold text-sm mb-2">Collections</h3>
              {financials.length === 0 ? <p className="text-gray-500 text-xs">No entries yet.</p> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left pb-1.5 pr-2">Side Event</th>
                    <th className="text-left pb-1.5 pr-2">Type</th>
                    {!squadId && <th className="text-left pb-1.5 pr-2">Squad</th>}
                    <th className="text-center pb-1.5 pr-2">Entries</th>
                    <th className="text-right pb-1.5 pr-2">Cost</th>
                    <th className="text-right pb-1.5">Pot</th>
                  </tr></thead>
                  <tbody>
                    {financials.map((f, i) => (
                      <tr key={i} className="border-b border-gray-700/50">
                        <td className="py-1.5 pr-2">{f.name}</td>
                        <td className="py-1.5 pr-2 text-gray-400">{TYPE_LABELS[f.type] || f.type}</td>
                        {!squadId && <td className="py-1.5 pr-2 text-gray-400">{f.squad_name} {f.squad_date ? `(${fmtDate(f.squad_date)})` : ''}</td>}
                        <td className="py-1.5 text-center">{f.total_entries}</td>
                        <td className="py-1.5 text-right">${Number(f.cost).toFixed(2)}</td>
                        <td className="py-1.5 text-right text-green-400 font-semibold">${Number(f.total_pot).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={squadId ? 4 : 5} className="pt-2 text-right font-bold text-gray-300">Total Pot</td>
                    <td className="pt-2 text-right font-bold text-green-300">${totalPot.toFixed(2)}</td>
                  </tr></tfoot>
                </table>
              )}
            </div>
            <div>
              <h3 className="text-gray-300 font-semibold text-sm mb-2">Payouts</h3>
              {payouts.length === 0 ? <p className="text-gray-500 text-xs">No payouts recorded yet.</p> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left pb-1.5 pr-2">Recipient</th>
                    <th className="text-left pb-1.5 pr-2">Side Event</th>
                    <th className="text-left pb-1.5 pr-2">Type</th>
                    {!squadId && <th className="text-left pb-1.5 pr-2">Squad</th>}
                    <th className="text-right pb-1.5 pr-2">Amount</th>
                    <th className="text-left pb-1.5 pr-2">Date</th>
                    <th className="text-left pb-1.5 pr-2">Notes</th>
                    <th className="text-center pb-1.5">✓</th>
                  </tr></thead>
                  <tbody>
                    {groupedPayouts.map((g, gi) => {
                      const multi = g.rows.length > 1;
                      return (
                        <React.Fragment key={`grp-${g.rows[0].id}`}>
                          {g.rows.map((p, ri) => (
                            <tr key={p.id} className={`hover:bg-gray-700/25 ${multi ? 'bg-indigo-950/20' : ''} ${ri === 0 && gi > 0 ? 'border-t-2 border-gray-500' : 'border-t border-gray-700/40'}`}>
                              <td className="py-1.5 pr-2 font-medium text-white">{ri === 0 ? _fmtName(g.name) : <span className="text-gray-600 pl-3 text-[10px]">└─</span>}</td>
                              <td className="py-1.5 pr-2 text-gray-400">{p.se_name}</td>
                              <td className="py-1.5 pr-2 text-gray-400">{TYPE_LABELS[p.se_type] || p.se_type}</td>
                              {!squadId && <td className="py-1.5 pr-2 text-gray-400">{p.squad_name} {p.squad_date ? `(${fmtDate(p.squad_date)})` : ''}</td>}
                              <td className="py-1.5 pr-2 text-right font-bold text-red-400">${Number(p.amount).toFixed(2)}</td>
                              <td className="py-1.5 pr-2 text-gray-400">{p.payout_date ? new Date(p.payout_date).toLocaleDateString() : '—'}</td>
                              <td className="py-1.5 pr-2 text-gray-400">{p.notes || '—'}</td>
                              <td className="py-1.5 text-center">
                                <input type="checkbox" checked={!!p.checkmark} onChange={() => handleCheckmark(p.id, p.checkmark)} className="w-4 h-4 accent-green-500 cursor-pointer" />
                              </td>
                            </tr>
                          ))}
                          {multi && (
                            <tr className="border-t border-emerald-700/50 bg-emerald-950/30">
                              <td colSpan={squadId ? 5 : 6} className="py-1 text-right text-[10px] font-semibold text-emerald-300 pr-2">Subtotal — {_fmtName(g.name)}</td>
                              <td className="py-1 pr-2 text-right font-bold text-emerald-400 text-[10px]">${g.total.toFixed(2)}</td>
                              <td />
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={squadId ? 4 : 5} className="pt-2 text-right font-bold text-gray-300">Total Paid Out</td>
                    <td className="pt-2 text-right font-bold text-yellow-300">${totalOut.toFixed(2)}</td>
                    <td colSpan={2} />
                  </tr></tfoot>
                </table>
              )}
            </div>
            <div className="border-t border-gray-700 pt-3 flex flex-wrap gap-5 text-sm">
              <span className="text-gray-400">Collected: <span className="text-green-300 font-semibold">${totalPot.toFixed(2)}</span></span>
              <span className="text-gray-400">Paid Out: <span className="text-yellow-300 font-semibold">${totalOut.toFixed(2)}</span></span>
              <span className="text-gray-400">Balance: <span className={`font-semibold ${totalPot - totalOut >= 0 ? 'text-blue-300' : 'text-red-400'}`}>${(totalPot - totalOut).toFixed(2)}</span></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── EntriesPanel ──────────────────────────────────────────────────────── */
const EntriesPanel: React.FC<{ sideEvent: AdminSideEvent; leagueId: number; onRefresh: () => void }> = ({ sideEvent, leagueId, onRefresh }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [allLeagueBowlers, setAllLeagueBowlers] = useState<Bowler[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [allSearch, setAllSearch] = useState('');
  const [allResults, setAllResults] = useState<Bowler[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');
  const allSearchTmr = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const r = await fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { headers: authHeader() });
      if (r.ok) setEntries(await r.json());
    } catch (e) { console.error(e); } finally { setLoadingEntries(false); }
  }, [sideEvent.id]);

  useEffect(() => {
    loadEntries();
    const eqs: string[] = [];
    if (sideEvent.gender === 'F') eqs.push('gender=F');
    if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
    if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
    const eqStr = eqs.length ? '?' + eqs.join('&') : '';
    fetch(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/all${eqStr}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : []).then(setAllLeagueBowlers).catch(() => {});
  }, [loadEntries, leagueId]);

  useEffect(() => {
    if (!allSearch.trim()) { setAllResults([]); return; }
    if (allSearchTmr.current) clearTimeout(allSearchTmr.current);
    allSearchTmr.current = setTimeout(async () => {
      setLoadingAll(true);
      try {
        const eqs: string[] = [`query=${encodeURIComponent(allSearch)}`];
        if (sideEvent.gender === 'F') eqs.push('gender=F');
        if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
        if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
        const r = await fetch(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/search?${eqs.join('&')}`, { headers: authHeader() });
        if (r.ok) setAllResults(await r.json());
      } catch (e) { console.error(e); } finally { setLoadingAll(false); }
    }, 350);
  }, [allSearch, leagueId]);

  const enteredIds = new Set(entries.map(e => e.bowler_id));
  const filteredGroup = allLeagueBowlers.filter(b => b.name.toLowerCase().includes(groupSearch.toLowerCase()));
  const sortedEntries = [...entries].sort((a, b) => _lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const filteredEntries = entrySearch.trim()
    ? sortedEntries.filter(e => _bowlerMatch(entrySearch, e.bowler_name) > 0)
    : sortedEntries;

  const addEntry = async (bowlerId: number) => {
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { bowlerId, paid: true }, { headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); }
  };
  const removeEntry = async (bowlerId: number) => {
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { data: { bowlerId }, headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-gray-400">{entries.length} entries</span>
        {Number(sideEvent.cost) > 0 && <span className="text-green-400 font-medium">Pot: ${(entries.length * Number(sideEvent.cost)).toFixed(2)}</span>}
      </div>

      {/* Add entry search bars — OUTSIDE enrolled section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-900 rounded-lg p-3">
          <p className="text-xs text-gray-400 font-semibold mb-2">To add an entry — League Bowlers</p>
          <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Filter…" className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
          {groupSearch.trim() && (
            <div className="space-y-0.5 max-h-52 overflow-y-auto mt-2">
              {filteredGroup.map(b => (
                <div key={b.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-800">
                  <span className="text-xs text-white">{b.name}</span>
                  {enteredIds.has(b.id) ? <span className="text-xs text-green-400">✓</span> : <button onClick={() => addEntry(b.id)} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>}
                </div>
              ))}
              {filteredGroup.length === 0 && <p className="text-xs text-gray-500 px-2 py-1">None found.</p>}
            </div>
          )}
        </div>
        <div className="bg-gray-900 rounded-lg p-3">
          <p className="text-xs text-gray-400 font-semibold mb-2">To add an entry — Search All Bowlers</p>
          <input value={allSearch} onChange={e => setAllSearch(e.target.value)} placeholder="Search by name…" className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
          {allSearch.trim() && (
            <div className="space-y-0.5 max-h-52 overflow-y-auto mt-2">
              {loadingAll ? <p className="text-xs text-gray-500 px-2">Searching…</p> : allResults.map(b => (
                <div key={b.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-800">
                  <span className="text-xs text-white">{b.name}</span>
                  {enteredIds.has(b.id) ? <span className="text-xs text-green-400">✓</span> : <button onClick={() => addEntry(b.id)} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>}
                </div>
              ))}
              {!loadingAll && !allResults.length && <p className="text-xs text-gray-500 px-2">No results.</p>}
            </div>
          )}
        </div>
      </div>

      {/* Enrolled bowlers — collapsible, default collapsed */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <button onClick={() => setShowEntries(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/40 hover:bg-gray-700/60 text-sm text-gray-300 transition-colors">
          <div className="flex items-center gap-3">
            <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${showEntries ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium">Enrolled Bowlers ({entries.length})</span>
          </div>
          <span className="text-xs text-gray-500">{showEntries ? 'collapse' : 'expand'}</span>
        </button>
        {showEntries && (
          <div className="p-3 space-y-3 bg-gray-900">
            <div>
              <label className="block text-sm font-bold text-white mb-2">Find Enrolled Bowler</label>
              <input value={entrySearch} onChange={e => setEntrySearch(e.target.value)}
                placeholder="Search enrolled bowlers..."
                className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
            </div>
            {loadingEntries ? <p className="text-xs text-gray-500">Loading…</p> : filteredEntries.length === 0 ? (
              <p className="text-xs text-gray-500">{entries.length === 0 ? 'No entries yet.' : 'No enrolled bowlers match your search.'}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
                {filteredEntries.map(e => (
                  <div key={e.bowler_id} className="flex items-center justify-between px-2 py-1 bg-gray-800 rounded">
                    <span className="text-xs text-white">{_fmtName(e.bowler_name)}</span>
                    <button onClick={() => removeEntry(e.bowler_id)} className="text-xs text-red-400 hover:text-red-300 ml-2">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── CleanGamePanel ────────────────────────────────────────────────────── */
const CleanGamePanel: React.FC<{ sideEvent: AdminSideEvent; leagueId: number; onRefresh: () => void }> = ({ sideEvent, leagueId, onRefresh }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [allLeagueBowlers, setAllLeagueBowlers] = useState<Bowler[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [payoutEntry, setPayoutEntry] = useState<Entry | null>(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const r = await fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { headers: authHeader() });
      if (r.ok) setEntries(await r.json());
    } catch (e) { console.error(e); } finally { setLoadingEntries(false); }
  }, [sideEvent.id]);

  useEffect(() => {
    loadEntries();
    const eqs: string[] = [];
    if (sideEvent.gender === 'F') eqs.push('gender=F');
    if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
    if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
    const eqStr = eqs.length ? '?' + eqs.join('&') : '';
    fetch(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/all${eqStr}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : []).then(setAllLeagueBowlers).catch(() => {});
  }, [loadEntries, leagueId]);

  const enteredIds = new Set(entries.map(e => e.bowler_id));
  const filteredGroup = allLeagueBowlers.filter(b => b.name.toLowerCase().includes(groupSearch.toLowerCase()));
  const sortedEntries = [...entries].sort((a, b) => _lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const filteredEntries = entrySearch.trim()
    ? sortedEntries.filter(e => _bowlerMatch(entrySearch, e.bowler_name) > 0)
    : sortedEntries;

  const addEntry = async (bowlerId: number) => {
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { bowlerId, paid: true }, { headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); }
  };
  const removeEntry = async (bowlerId: number) => {
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { data: { bowlerId }, headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-gray-400">{entries.length} entries</span>
        {Number(sideEvent.cost) > 0 && <span className="text-green-400 font-medium">Pot: ${(entries.length * Number(sideEvent.cost)).toFixed(2)}</span>}
      </div>

      {/* To add an entry — League Bowlers (OUTSIDE enrolled) */}
      <div className="bg-gray-900 rounded-lg p-3">
        <p className="text-xs text-gray-400 font-semibold mb-2">To add an entry — League Bowlers</p>
        <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Filter…" className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
        {groupSearch.trim() && (
          <div className="space-y-0.5 max-h-52 overflow-y-auto mt-2">
            {filteredGroup.map(b => (
              <div key={b.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-800">
                <span className="text-xs text-white">{b.name}</span>
                {enteredIds.has(b.id) ? <span className="text-xs text-green-400">✓</span> : <button onClick={() => addEntry(b.id)} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>}
              </div>
            ))}
            {filteredGroup.length === 0 && <p className="text-xs text-gray-500 px-2 py-1">None found.</p>}
          </div>
        )}
      </div>

      {/* Enrolled bowlers — collapsible with Pay/Remove, default collapsed */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <button onClick={() => setShowEntries(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/40 hover:bg-gray-700/60 text-sm text-gray-300 transition-colors">
          <div className="flex items-center gap-3">
            <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${showEntries ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium">Enrolled Bowlers — Pay Out Per Entry ({entries.length})</span>
          </div>
          <span className="text-xs text-gray-500">{showEntries ? 'collapse' : 'expand'}</span>
        </button>
        {showEntries && (
          <div className="p-3 space-y-3 bg-gray-900">
            <div>
              <label className="block text-sm font-bold text-white mb-2">Find Enrolled Bowler</label>
              <input value={entrySearch} onChange={e => setEntrySearch(e.target.value)}
                placeholder="Search enrolled bowlers..."
                className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
            </div>
            {loadingEntries ? <p className="text-xs text-gray-500">Loading…</p> : filteredEntries.length === 0 ? (
              <p className="text-xs text-gray-500">{entries.length === 0 ? 'No entries yet.' : 'No enrolled bowlers match your search.'}</p>
            ) : (
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {filteredEntries.map(e => (
                  <div key={e.bowler_id} className="flex items-center justify-between px-2 py-1.5 bg-gray-800 rounded">
                    <span className="text-xs text-white">{_fmtName(e.bowler_name)}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setPayoutEntry(e); setPayoutOpen(true); }} className="text-xs px-2 py-0.5 bg-yellow-700/60 hover:bg-yellow-700 text-yellow-200 rounded">Pay</button>
                      <button onClick={() => removeEntry(e.bowler_id)} className="text-xs text-red-400 hover:text-red-300">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {payoutOpen && payoutEntry && (
        <BookiePayoutModal
          isOpen={payoutOpen}
          onClose={() => { setPayoutOpen(false); setPayoutEntry(null); }}
          bowlerId={payoutEntry.bowler_id}
          bowlerName={payoutEntry.bowler_name}
          sideEventId={sideEvent.id}
          sideEventType="clean_game"
          leagueId={leagueId}
          defaultAmount={Number(sideEvent.cost)}
          onPayoutComplete={() => { setPayoutOpen(false); setPayoutEntry(null); onRefresh(); }}
        />
      )}
    </div>
  );
};

/* ─── BracketTree ───────────────────────────────────────────────────────── */
const BracketTree: React.FC<{ bracket: BracketData; totalRounds: number }> = ({ bracket, totalRounds }) => {
  const mu = (m: BracketMatchupData | undefined) => {
    if (!m) return (
      <div style={{ width: 112, border: '1px solid #374151', borderRadius: 3 }}>
        <div style={{ padding: '2px 5px', borderBottom: '1px solid #374151', fontSize: 9, color: '#6b7280', fontStyle: 'italic' }}>TBD</div>
        <div style={{ padding: '2px 5px', fontSize: 9, color: '#6b7280', fontStyle: 'italic' }}>TBD</div>
      </div>
    );
    const e1w = m.winner_entry_id === m.entry1_id && m.winner_entry_id != null;
    const e2w = m.winner_entry_id === m.entry2_id && m.winner_entry_id != null;
    const row = (name: string | null, score: number | null, won: boolean, bye: boolean) => (
      <div style={{ padding: '2px 5px', fontSize: 9, background: won ? 'rgba(21,128,61,0.3)' : 'transparent', fontWeight: won ? 700 : 400, color: bye ? '#6b7280' : won ? '#86efac' : '#d1d5db', fontStyle: bye ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', justifyContent: 'space-between' }}>
        <span>{bye ? 'BYE' : (name || 'TBD')}</span>
        {!bye && score != null && <span style={{ opacity: 0.7, marginLeft: 4 }}>{score}</span>}
      </div>
    );
    return (
      <div style={{ width: 112, border: '1px solid #374151', borderRadius: 3, overflow: 'hidden' }}>
        {row(m.entry1_name, m.entry1_score, e1w, false)}
        <div style={{ borderTop: '1px solid #374151' }}>
          {row(m.is_bye ? 'BYE' : m.entry2_name, m.entry2_score, e2w, m.is_bye)}
        </div>
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
      {Array.from({ length: totalRounds }, (_, ri) => {
        const rn = ri + 1;
        const round = bracket.rounds.find(r => r.round_number === rn);
        const slotCount = Math.pow(2, totalRounds - rn);
        const slotH = Math.pow(2, rn - 1) * 44;
        const label = rn === totalRounds ? 'Final' : `R${rn}${round ? ` G${round.game_number}` : ''}`;
        return (
          <div key={rn} style={{ flexShrink: 0 }}>
            <div style={{ textAlign: 'center', fontSize: 9, color: '#9ca3af', marginBottom: 3, paddingBottom: 2, borderBottom: '1px solid #374151' }}>{label}</div>
            {Array.from({ length: slotCount }, (_, si) => (
              <div key={si} style={{ height: slotH, display: 'flex', alignItems: 'center' }}>
                {mu(round?.matchups?.[si])}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/* ─── BracketEntriesPanel ───────────────────────────────────────────────── */
const BracketEntriesPanel: React.FC<{ sideEvent: AdminSideEvent; leagueId: number; onRefresh: () => void }> = ({ sideEvent, leagueId, onRefresh }) => {
  const [tab, setTab] = useState<'entries' | 'brackets' | 'cashers'>('entries');
  const [entries, setEntries] = useState<BracketEntry[]>([]);
  const [brackets, setBrackets] = useState<BracketData[]>([]);
  const [totalRounds, setTotalRounds] = useState(3);
  const [cashers, setCashers] = useState<any[]>([]);
  const [refunds, setRefunds] = useState<any[]>([]);
  const [allLeagueBowlers, setAllLeagueBowlers] = useState<Bowler[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [loadingBrackets, setLoadingBrackets] = useState(false);
  const [working, setWorking] = useState(false);
  const [pullType, setPullType] = useState<'scratch' | 'handicap'>('scratch');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addCounts, setAddCounts] = useState<Record<number, number>>({});
  const [bracketsExist, setBracketsExist] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const r = await fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/bracket-entries?squadId=${sideEvent.squad_id}`, { headers: authHeader() });
      if (r.ok) { const d = await r.json(); setEntries(Array.isArray(d) ? d : (d.bowlerEntries || [])); }
    } catch (e) { console.error(e); } finally { setLoadingEntries(false); }
  }, [sideEvent.id, sideEvent.squad_id]);

  const loadBrackets = useCallback(async () => {
    setLoadingBrackets(true);
    try {
      const [br, ca, ex] = await Promise.all([
        fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/brackets-display`, { headers: authHeader() }),
        fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/bracket-cashers`, { headers: authHeader() }),
        fetch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/existing-brackets`, { headers: authHeader() }),
      ]);
      if (br.ok) { const d = await br.json(); setBrackets(d.brackets || []); setTotalRounds(d.totalRounds ?? 3); }
      if (ca.ok) { const d = await ca.json(); setCashers(d.cashers || []); setRefunds(d.refunds || []); }
      if (ex.ok) { const d = await ex.json(); setBracketsExist((d.brackets || []).length > 0); setIsPublished(!!d.published); }
    } catch (e) { console.error(e); } finally { setLoadingBrackets(false); }
  }, [sideEvent.id]);

  useEffect(() => {
    loadEntries();
    loadBrackets();
    const eqs: string[] = [];
    if (sideEvent.gender === 'F') eqs.push('gender=F');
    if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
    if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
    const eqStr = eqs.length ? '?' + eqs.join('&') : '';
    fetch(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/all${eqStr}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : []).then(setAllLeagueBowlers).catch(() => {});
  }, [loadEntries, loadBrackets, leagueId]);

  useEffect(() => {
    if (tab === 'brackets' || tab === 'cashers') loadBrackets();
  }, [tab, loadBrackets]);

  const filteredGroup = allLeagueBowlers.filter(b => b.name.toLowerCase().includes(groupSearch.toLowerCase()));
  const enteredIds = new Set(entries.map(e => e.bowler_id));

  const addBowler = async (bowlerId: number) => {
    const entryCount = addCounts[bowlerId] || 1;
    setWorking(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/enter-bowler`, { bowlerId, entryCount }, { headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); } finally { setWorking(false); }
  };

  const removeBowler = async (bowlerId: number) => {
    setWorking(true);
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/bracket-entry`, { data: { bowlerId }, headers: authHeader() });
      await loadEntries(); onRefresh();
    } catch (e) { console.error(e); } finally { setWorking(false); }
  };

  const generateBrackets = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/generate-brackets`, {}, { headers: authHeader() });
      await loadBrackets(); await loadEntries(); onRefresh(); setTab('brackets');
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to generate brackets';
      alert(msg);
    } finally { setWorking(false); }
  };

  const deleteBrackets = async () => {
    setWorking(true);
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/delete-brackets`, { headers: authHeader() });
      setBrackets([]); setCashers([]); setRefunds([]); setBracketsExist(false); setIsPublished(false); setShowDeleteConfirm(false); setTab('entries');
      await loadEntries();
    } catch (e) { console.error(e); } finally { setWorking(false); }
  };

  const publishBrackets = async (publish: boolean) => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/publish-brackets`, { publish }, { headers: authHeader() });
      setIsPublished(publish);
    } catch (e) { console.error(e); } finally { setWorking(false); }
  };

  const pullScores = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/pull-bracket-scores`, { useHandicap: pullType === 'handicap' }, { headers: authHeader() });
      await loadBrackets();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Failed to pull scores';
      alert(msg);
    } finally { setWorking(false); }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-400">{entries.length} entries · {brackets.length} brackets</span>
        {Number(sideEvent.cost) > 0 && <span className="text-green-400">Pot: ${(entries.length * Number(sideEvent.cost)).toFixed(2)}</span>}
        <div className="ml-auto flex flex-wrap gap-1">
          {bracketsExist && <>
            <button onClick={() => printBrackets(sideEvent.league_name, sideEvent.name, brackets, totalRounds, sideEvent.cost)} className="px-2 py-1 bg-green-700 hover:bg-green-600 text-xs text-white rounded">🖨️ Brackets</button>
            <button onClick={() => printBracketEntries(sideEvent.league_name, sideEvent.name, entries, brackets)} className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded">🖨️ Entries</button>
            <button onClick={() => printAliveBowlers(sideEvent.league_name, sideEvent.name, brackets)} className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded">🖨️ Alive</button>
            <button onClick={() => printBracketCashers(sideEvent.league_name, sideEvent.name, cashers, refunds, sideEvent.cost)} className="px-2 py-1 bg-yellow-700 hover:bg-yellow-600 text-xs text-white rounded">🖨️ Cashers</button>
          </>}
        </div>
      </div>
      <div className="flex gap-1 border-b border-gray-700">
        {(['entries', 'brackets', 'cashers'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs rounded-t transition-colors ${tab === t ? 'bg-gray-700 text-white font-semibold' : 'text-gray-500 hover:text-white'}`}>
            {t === 'entries' ? `Entries (${entries.length})` : t === 'brackets' ? `Brackets (${brackets.length})` : `Print Cashers (${cashers.length})`}
          </button>
        ))}
      </div>

      {tab === 'entries' && (
        <div className="space-y-3">
          {/* To add an entry — League Bowlers (OUTSIDE enrolled) */}
          <div className="bg-gray-900 rounded-lg p-3">
            <p className="text-xs text-gray-400 font-semibold mb-2">To add an entry — League Bowlers</p>
            <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Filter…" className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
            {groupSearch.trim() && (
              <div className="space-y-0.5 max-h-48 overflow-y-auto mt-2">
                {filteredGroup.map(b => (
                  <div key={b.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-800">
                    <span className="text-xs text-white">{b.name}</span>
                    {enteredIds.has(b.id) ? <span className="text-xs text-green-400">✓</span> : (
                      <div className="flex items-center gap-1">
                        <select value={addCounts[b.id] || 1} onChange={e => setAddCounts(p => ({ ...p, [b.id]: Number(e.target.value) }))} className="bg-gray-700 text-white text-xs px-1 py-0.5 rounded w-12">
                          {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}x</option>)}
                        </select>
                        <button onClick={() => addBowler(b.id)} disabled={working} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">+ Add</button>
                      </div>
                    )}
                  </div>
                ))}
                {filteredGroup.length === 0 && <p className="text-xs text-gray-500 px-2 py-1">None found.</p>}
              </div>
            )}
          </div>

          {/* Enrolled bowlers — collapsible, default collapsed, prominent toggle for brackets */}
          <div className="border-2 border-indigo-600/50 rounded-xl overflow-hidden">
            <button onClick={() => setShowEntries(s => !s)}
              className="w-full flex items-center justify-between px-4 py-3 bg-indigo-900/40 hover:bg-indigo-900/60 transition-colors">
              <div className="flex items-center gap-3">
                <svg className={`w-5 h-5 text-indigo-300 flex-shrink-0 transition-transform ${showEntries ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-sm font-bold text-indigo-200">Enrolled Bowlers ({entries.length})</span>
              </div>
              <span className="text-xs text-indigo-400">{showEntries ? 'Click to collapse' : 'Click to expand'}</span>
            </button>
            {showEntries && (
              <div className="p-3 space-y-3 bg-gray-800/40">
                <div>
                  <label className="block text-sm font-bold text-white mb-2">Find Enrolled Bowler</label>
                  <input value={entrySearch} onChange={e => setEntrySearch(e.target.value)}
                    placeholder="Search enrolled bowlers..."
                    className="w-full bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none" />
                </div>
                {loadingEntries ? <p className="text-xs text-gray-500">Loading…</p> : entries.length === 0 ? (
                  <p className="text-xs text-gray-500">No entries yet.</p>
                ) : (
                  <div className="space-y-0.5 max-h-48 overflow-y-auto">
                    {[...entries]
                      .sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)))
                      .filter(e => !entrySearch.trim() || e.name.toLowerCase().includes(entrySearch.toLowerCase()))
                      .map(e => (
                        <div key={e.bowler_id} className="flex items-center justify-between px-2 py-1 bg-gray-800 rounded">
                          <span className="text-xs text-white">{_fmtName(e.name)} <span className="text-gray-500">×{e.entryCount}</span></span>
                          <button onClick={() => removeBowler(e.bowler_id)} disabled={working} className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50">✕</button>
                        </div>
                      ))
                    }
                    {entrySearch.trim() && entries.filter(e => e.name.toLowerCase().includes(entrySearch.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-500 px-2 py-2">No enrolled bowlers match your search.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={generateBrackets} disabled={working || entries.length < 2} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-xs text-white rounded-lg disabled:opacity-50">
              {working ? 'Working…' : bracketsExist ? '↻ Regenerate Brackets' : 'Generate Brackets'}
            </button>
            {bracketsExist && (
              isPublished
                ? <button onClick={() => publishBrackets(false)} disabled={working} className="px-3 py-1.5 bg-yellow-700 hover:bg-yellow-600 text-xs text-white rounded-lg disabled:opacity-50">Unpublish</button>
                : <button onClick={() => publishBrackets(true)} disabled={working} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-xs text-white rounded-lg disabled:opacity-50">Publish to Bowlers</button>
            )}
            {bracketsExist && !showDeleteConfirm && <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-1.5 bg-red-900/50 hover:bg-red-800 text-xs text-white rounded-lg">Delete Brackets</button>}
            {showDeleteConfirm && <>
              <span className="text-xs text-red-400">Confirm delete all brackets?</span>
              <button onClick={deleteBrackets} disabled={working} className="px-3 py-1.5 bg-red-600 text-xs text-white rounded-lg">Yes</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 bg-gray-700 text-xs text-white rounded-lg">Cancel</button>
            </>}
          </div>
        </div>
      )}

      {tab === 'brackets' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">Pull scores from:</span>
            <select value={pullType} onChange={e => setPullType(e.target.value as 'scratch' | 'handicap')} className="bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 outline-none">
              <option value="scratch">Scratch</option>
              <option value="handicap">Handicap</option>
            </select>
            <button onClick={pullScores} disabled={working || brackets.length === 0} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-xs text-white rounded-lg disabled:opacity-50">
              {working ? 'Pulling…' : '↻ Pull Scores'}
            </button>
          </div>
          {loadingBrackets ? <p className="text-xs text-gray-500 py-4">Loading brackets…</p> : brackets.length === 0 ? (
            <p className="text-xs text-gray-500 py-4">No brackets yet. Generate from Entries tab.</p>
          ) : (
            <div className="space-y-5">
              {brackets.map(b => (
                <div key={b.id} className="bg-gray-900 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-300 mb-2">Bracket #{b.bracket_number}</p>
                  <BracketTree bracket={b} totalRounds={totalRounds} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'cashers' && (
        <div className="space-y-1">
          {cashers.length === 0 ? (
            <p className="text-xs text-gray-500 py-4">No cashers yet — pull scores first.</p>
          ) : (
            cashers.map((c: any, i: number) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-900 rounded-lg">
                <div>
                  <span className="text-sm text-white">{c.bowler_name}</span>
                  {c.payout_notes && <span className="text-xs text-gray-400 ml-2">{c.payout_notes}</span>}
                </div>
                <span className="text-sm text-green-400 font-semibold">${Number(c.total_amount).toFixed(2)}</span>
              </div>
            ))
          )}
          {refunds.length > 0 && (
            <div className="mt-4 border-t border-gray-700/60 pt-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Refunds Issued at Generation</p>
              {refunds.map((r: any, i: number) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-900 rounded-lg mb-1">
                  <span className="text-sm text-white">{r.bowler_name}</span>
                  <span className="text-sm text-yellow-400 font-semibold">${Number(r.refund_amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Admin Doubles Panel ────────────────────────────────────────────────── */
const AdminDoublesPanel: React.FC<{
  sideEvent: AdminSideEvent;
  leagueId: number;
  onRefresh: () => void;
}> = ({ sideEvent, leagueId, onRefresh }) => {
  // Entry state
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [addingBowler, setAddingBowler] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);

  // League bowler search (local filter)
  const [leagueBowlers, setLeagueBowlers] = useState<Bowler[]>([]);
  const [leagueSearch, setLeagueSearch] = useState('');

  // All bowlers search
  const [allSearch, setAllSearch] = useState('');
  const [allResults, setAllResults] = useState<Bowler[]>([]);
  const allSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Doubles state
  const [doubles, setDoubles] = useState<{ double_id: number; bowlers: any[] }[]>([]);
  const [doublesGenerated, setDoublesGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [generateError, setGenerateError] = useState('');

  // Scores state
  const [scores, setScores] = useState<{ double_id: number; total_score: number; bowlers: any[] }[]>([]);
  const [useHandicap, setUseHandicap] = useState(!!sideEvent.hdcp);
  const [loadingScores, setLoadingScores] = useState(false);

  // Swap state
  const [selectedParticipant, setSelectedParticipant] = useState<{ participant_id: number; double_id: number; bowler_name: string } | null>(null);
  const [swapping, setSwapping] = useState(false);

  // UI
  const [expandedDoubles, setExpandedDoubles] = useState<Set<number>>(new Set());
  const [showDoubles, setShowDoubles] = useState(true);
  const [showEntries, setShowEntries] = useState(true);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const r = await axios.get(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`, { headers: authHeader() });
      setEntries(r.data);
    } catch {} finally { setLoadingEntries(false); }
  }, [sideEvent.id]);

  const loadDoubles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/doubles`, { headers: authHeader() });
      const raw = r.data.doubles as Record<string, any[]>;
      const d = (Object.entries(raw) as [string, any[]][])
        .filter(([k]: [string, any[]]) => k !== 'unpaired')
        .sort(([a]: [string, any[]], [b]: [string, any[]]) => parseInt(a) - parseInt(b))
        .map(([k, bowlers]: [string, any[]]) => ({ double_id: parseInt(k), bowlers }));
      setDoubles(d);
      setDoublesGenerated(d.length > 0);
    } catch {}
  }, [sideEvent.id]);

  const loadScores = useCallback(async (hdcp?: boolean) => {
    const hFlag = hdcp !== undefined ? hdcp : useHandicap;
    setLoadingScores(true);
    try {
      const r = await axios.get(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/doubles-scores?handicap=${hFlag}`, { headers: authHeader() });
      setScores(r.data.scores || []);
    } catch {} finally { setLoadingScores(false); }
  }, [sideEvent.id, useHandicap]);

  useEffect(() => {
    loadEntries();
    loadDoubles();
    axios.get(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/all`, { headers: authHeader() })
      .then(r => setLeagueBowlers(r.data)).catch(() => {});
  }, [loadEntries, loadDoubles, leagueId]);

  useEffect(() => { if (doublesGenerated) loadScores(); }, [doublesGenerated]); // eslint-disable-line

  useEffect(() => {
    clearTimeout(allSearchTimeout.current);
    if (!allSearch.trim()) { setAllResults([]); return; }
    allSearchTimeout.current = setTimeout(() => {
      axios.get(`${API}/admin/manage-side-events/leagues/${leagueId}/bowlers/search?query=${encodeURIComponent(allSearch)}`, { headers: authHeader() })
        .then(r => setAllResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(allSearchTimeout.current);
  }, [allSearch, leagueId]);

  const filteredLeagueBowlers = leagueSearch.trim().length >= 1
    ? leagueBowlers.filter(b => b.name.toLowerCase().includes(leagueSearch.toLowerCase()))
    : [];

  const addEntry = async (bowler: Bowler) => {
    setAddingBowler(bowler.id);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`,
        { bowlerId: bowler.id, paid: true }, { headers: authHeader() });
      setLeagueSearch('');
      setAllSearch(''); setAllResults([]);
      loadEntries(); onRefresh();
    } catch {} finally { setAddingBowler(null); }
  };

  const removeEntry = async (entry: Entry) => {
    setRemoving(entry.bowler_id);
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/entries`,
        { data: { bowlerId: entry.bowler_id }, headers: authHeader() });
      loadEntries(); onRefresh();
    } catch {} finally { setRemoving(null); }
  };

  const handleGenerateDoubles = async () => {
    setGenerating(true); setGenerateMsg(''); setGenerateError('');
    try {
      const res = await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/generate-doubles`, {}, { headers: authHeader() });
      setGenerateMsg(res.data?.message || 'Doubles generated!');
      await loadEntries();
      await loadDoubles();
      await loadScores(useHandicap);
      onRefresh();
    } catch (e: any) {
      setGenerateError(e?.response?.data?.error || 'Failed to generate doubles');
    } finally { setGenerating(false); }
  };

  const handleParticipantClick = (p: { participant_id: number; double_id: number; bowler_name: string }) => {
    if (!selectedParticipant) {
      setSelectedParticipant(p);
    } else if (selectedParticipant.participant_id === p.participant_id) {
      setSelectedParticipant(null);
    } else if (selectedParticipant.double_id === p.double_id) {
      setSelectedParticipant(p);
    } else {
      handleSwap(selectedParticipant.participant_id, p.participant_id);
    }
  };

  const handleSwap = async (pid1: number, pid2: number) => {
    setSwapping(true);
    try {
      await axios.post(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/swap-doubles`,
        { participantId1: pid1, participantId2: pid2 }, { headers: authHeader() });
      setSelectedParticipant(null);
      await loadDoubles();
      await loadScores(useHandicap);
    } catch {} finally { setSwapping(false); }
  };

  const toggleHandicap = () => {
    const next = !useHandicap;
    setUseHandicap(next);
    if (doublesGenerated) loadScores(next);
    axios.patch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/hdcp`,
      { hdcp: next }, { headers: authHeader() }).catch(() => {});
  };

  const toggleDouble = (id: number) => setExpandedDoubles(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const getScoreForDouble = (id: number) => scores.find(s => s.double_id === id);
  const totalPot = entries.length * Number(sideEvent.cost);

  return (
    <div className="px-4 py-3">
      {/* Header toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{entries.length} enrolled</span>
          {totalPot > 0 && <span className="text-green-400 font-semibold text-sm">${totalPot.toFixed(2)} pot</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleGenerateDoubles} disabled={generating || (entries.length === 0 && !doublesGenerated)}
            className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {generating && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {doublesGenerated ? 'Regenerate Doubles' : 'Generate Doubles'}
          </button>
          {doublesGenerated && (
            <>
              <button onClick={() => loadScores(useHandicap)} disabled={loadingScores}
                className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                {loadingScores && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Refresh Scores
              </button>
              <button onClick={toggleHandicap}
                className={`text-xs px-3 py-1.5 rounded-lg text-white font-semibold transition-colors ${useHandicap ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-600 hover:bg-gray-500'}`}>
                Hdcp {useHandicap ? 'ON' : 'OFF'}
              </button>
            </>
          )}
        </div>
      </div>

      {generateMsg && <div className="mb-3 text-xs px-3 py-2 bg-green-900/30 border border-green-600/40 text-green-300 rounded-lg">{generateMsg}</div>}
      {generateError && <div className="mb-3 text-xs px-3 py-2 bg-red-900/30 border border-red-600/40 text-red-300 rounded-lg">{generateError}</div>}

      {selectedParticipant && (
        <div className="mb-3 text-xs px-3 py-2 bg-yellow-900/30 border border-yellow-600/40 text-yellow-200 rounded-lg flex items-center justify-between">
          <span>Selected: <strong>{selectedParticipant.bowler_name}</strong> — click a bowler from a different double to swap</span>
          <button onClick={() => setSelectedParticipant(null)} className="text-yellow-400 hover:text-white ml-2">✕</button>
        </div>
      )}

      {/* Entries section (collapsible) */}
      <div className="mb-4 border border-gray-700 rounded-xl overflow-hidden">
        <button onClick={() => setShowEntries(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/40 hover:bg-gray-700/60 text-sm text-gray-300 transition-colors">
          <span className="font-medium">Entries ({entries.length})</span>
          <svg className={`w-4 h-4 transition-transform ${showEntries ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showEntries && (
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Search from league bowlers */}
              <div>
                <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search from league</label>
                <div className="relative">
                  <input value={leagueSearch} onChange={e => setLeagueSearch(e.target.value)} placeholder="Type name..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                  {leagueSearch && (
                    <button onClick={() => setLeagueSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                  {filteredLeagueBowlers.length > 0 && (
                    <div className="absolute z-20 top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto">
                      {filteredLeagueBowlers.slice(0, 10).map(b => (
                        <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 border-b border-gray-700 last:border-0 text-left transition-colors">
                          <span className="text-white text-sm font-medium">{b.name}</span>
                          {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs ml-2 font-semibold">${Number(sideEvent.cost).toFixed(2)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Search from all bowlers */}
              <div>
                <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search all bowlers</label>
                <div className="relative">
                  <input value={allSearch} onChange={e => setAllSearch(e.target.value)} placeholder="Search by name..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                  {allSearch && (
                    <button onClick={() => { setAllSearch(''); setAllResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                  {allResults.length > 0 && (
                    <div className="absolute z-20 top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                      {allResults.map(b => (
                        <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 border-b border-gray-700 last:border-0 text-left transition-colors">
                          <div>
                            <div className="text-white text-sm font-medium">{b.name}</div>
                            {b.email && <div className="text-gray-400 text-xs">{b.email}</div>}
                          </div>
                          {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs ml-2 font-semibold">${Number(sideEvent.cost).toFixed(2)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loadingEntries && <div className="flex justify-center py-4"><div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
            {!loadingEntries && entries.length === 0 && <div className="text-center text-gray-500 py-4 text-sm">No entries yet.</div>}
            {!loadingEntries && entries.length > 0 && (
              <div className="space-y-1">
                {entries.map((entry, i) => (
                  <div key={entry.id || i} className="flex items-center justify-between bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.paid ? 'bg-green-400' : 'bg-yellow-400'}`} />
                      <span className="text-white text-sm font-medium">{entry.bowler_name}</span>
                      {entry.paid
                        ? <span className="text-xs text-green-400">Paid · ${Number(sideEvent.cost).toFixed(2)}</span>
                        : <span className="text-xs text-yellow-400">Unpaid</span>}
                    </div>
                    <button onClick={() => removeEntry(entry)} disabled={removing === entry.bowler_id}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1 disabled:opacity-50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Doubles list */}
      {doublesGenerated && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowDoubles(s => !s)}
              className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity">
              <h4 className="text-sm font-semibold text-gray-300">Doubles ({doubles.length} pairs)</h4>
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${showDoubles ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {swapping && <div className="text-xs text-gray-400 flex items-center gap-1.5"><div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />Swapping...</div>}
          </div>
          {showDoubles && <div className="space-y-2">
            {doubles.map((dbl, rank) => {
              const scoreEntry = getScoreForDouble(dbl.double_id);
              const isExpanded = expandedDoubles.has(dbl.double_id);
              const realBowlers = dbl.bowlers.filter((b: any) => b.bowler_id !== 1);
              const hasBlind = dbl.bowlers.some((b: any) => b.bowler_id === 1);
              return (
                <div key={dbl.double_id} className="border border-gray-600/50 rounded-xl overflow-hidden">
                  <button onClick={() => toggleDouble(dbl.double_id)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-700/40 hover:bg-gray-700/60 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 font-mono w-6 text-right">{rank + 1}.</span>
                      <span className="text-sm font-medium text-white">
                        {realBowlers.map((b: any) => b.bowler_name).join(' & ')}
                        {hasBlind && <span className="text-gray-500 italic"> & Blind</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {scoreEntry && scoreEntry.total_score > 0 && (
                        <span className="text-sm font-mono font-semibold text-green-400">{scoreEntry.total_score}</span>
                      )}
                      <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 py-3 bg-gray-800/40 space-y-2">
                      {dbl.bowlers.map((bowler: any) => {
                        const bScore = scoreEntry?.bowlers.find((bs: any) => bs.participant_id === bowler.participant_id);
                        const isSelected = selectedParticipant?.participant_id === bowler.participant_id;
                        const isBlind = bowler.bowler_id === 1;
                        return (
                          <button key={bowler.participant_id}
                            onClick={() => !isBlind && handleParticipantClick({ participant_id: bowler.participant_id, double_id: dbl.double_id, bowler_name: bowler.bowler_name })}
                            disabled={swapping || isBlind}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-left
                              ${isSelected
                                ? 'bg-yellow-900/30 border-yellow-500 text-yellow-200'
                                : isBlind
                                  ? 'bg-gray-700/20 border-gray-700/40 text-gray-500 cursor-default'
                                  : selectedParticipant && selectedParticipant.double_id !== dbl.double_id
                                    ? 'bg-blue-900/20 border-blue-600/40 text-white hover:bg-blue-800/30 cursor-pointer'
                                    : 'bg-gray-700/30 border-gray-600/50 text-white hover:bg-gray-600/30 cursor-pointer'}`}>
                            <div className="flex items-center gap-2">
                              {isSelected
                                ? <span className="text-yellow-400 text-xs">●</span>
                                : !isBlind && <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>}
                              <span className="text-sm font-medium">{isBlind ? 'Blind (200/game)' : bowler.bowler_name}</span>
                            </div>
                            {bScore && (
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-mono text-gray-300">{bScore.score}</span>
                                {useHandicap && bScore.handicap > 0 && <span className="text-teal-400">+{bScore.handicap}</span>}
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {scoreEntry && scoreEntry.total_score > 0 && (
                        <div className="flex justify-end pt-1 border-t border-gray-700/50">
                          <span className="text-xs text-gray-400">
                            Total: <span className="font-mono text-white font-semibold">{scoreEntry.total_score}</span>
                            {useHandicap && <span className="text-teal-300 ml-1">(w/ hdcp)</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      )}
    </div>
  );
};

/* ─── Doubles Payout Modal ───────────────────────────────────────────────── */
const DoublesPayoutModal: React.FC<{
  bowlers: { bowler_id: number; bowler_name: string }[];
  sideEventId: number;
  leagueId: number;
  onClose: () => void;
  onComplete?: () => void;
}> = ({ bowlers, sideEventId, leagueId, onClose, onComplete }) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [statuses, setStatuses] = useState<Record<number, 'idle' | 'paying' | 'paid' | 'error'>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const parsedAmount = parseFloat(amount);
  const canSubmit = !isNaN(parsedAmount) && parsedAmount > 0 &&
    !Object.values(statuses).some(s => s === 'paying');
  const allPaid = bowlers.length > 0 && bowlers.every(b => statuses[b.bowler_id] === 'paid');

  const payBowler = async (bowler: { bowler_id: number; bowler_name: string }) => {
    setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'paying' }));
    setGlobalError(null);
    try {
      await axios.post(
        `${API}/admin/payouts/sidepot`,
        { sideEventId, leagueId, recipientType: 'bowler', recipientId: bowler.bowler_id, amount: parsedAmount, sideEventType: 'side_event', placement: 1, notes: notes.trim() || 'Doubles payout' },
        { headers: authHeader() }
      );
      setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'paid' }));
    } catch (e: any) {
      setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'error' }));
      setGlobalError(e?.response?.data?.message || e?.response?.data?.error || `Failed to pay ${bowler.bowler_name}`);
    }
  };

  const handlePayBoth = async () => {
    for (const b of bowlers) {
      if (statuses[b.bowler_id] !== 'paid') await payBowler(b);
    }
    onComplete?.();
  };

  useEffect(() => { if (allPaid) setTimeout(onClose, 1800); }, [allPaid, onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={e => { if (e.target === e.currentTarget && canSubmit) onClose(); }}>
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Pay Double</h2>
          <button onClick={onClose} disabled={!canSubmit} className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-40">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {allPaid ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-green-400 font-semibold text-lg">Both paid out!</p>
            <p className="text-gray-400 text-sm mt-1">${parsedAmount.toFixed(2)} each</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-5">
              {bowlers.map(b => {
                const st = statuses[b.bowler_id] ?? 'idle';
                return (
                  <div key={b.bowler_id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${st === 'paid' ? 'bg-green-900/30 border-green-700/50' : st === 'error' ? 'bg-red-900/30 border-red-700/50' : 'bg-gray-700/50 border-gray-600/50'}`}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-purple-600/60 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">{b.bowler_name.charAt(0).toUpperCase()}</div>
                      <span className="text-white text-sm font-medium">{b.bowler_name}</span>
                    </div>
                    <div>
                      {st === 'paid' && <span className="text-green-400 text-sm font-semibold">✔ Paid</span>}
                      {st === 'paying' && <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />}
                      {st === 'error' && <button onClick={() => payBowler(b)} disabled={isNaN(parsedAmount) || parsedAmount <= 0} className="text-xs px-2 py-1 bg-red-700 hover:bg-red-600 text-white rounded font-medium">Retry</button>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Amount per bowler</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">$</span>
                <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" autoFocus className="w-full pl-7 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-500" />
              </div>
              {!isNaN(parsedAmount) && parsedAmount > 0 && <p className="text-[11px] text-gray-500 mt-1">Total: ${(parsedAmount * bowlers.length).toFixed(2)} for {bowlers.length} bowler{bowlers.length !== 1 ? 's' : ''}</p>}
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Notes <span className="text-gray-600">(optional)</span></label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. Series winner, Squad 1…" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 resize-none placeholder-gray-500" />
            </div>
            {globalError && <div className="mb-4 px-3 py-2 bg-red-900/40 border border-red-500/40 rounded-lg text-red-300 text-xs">{globalError}</div>}
            <div className="flex gap-3">
              <button onClick={onClose} disabled={!canSubmit} className="flex-1 px-4 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm disabled:opacity-50">Cancel</button>
              <button onClick={handlePayBoth} disabled={!canSubmit} className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"><span>💰</span> Pay Both</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── SELivePanel ───────────────────────────────────────────────────────── */
const SELivePanel: React.FC<{ sideEvent: AdminSideEvent; leagueId: number; squadId: number; refreshKey: number }> = ({ sideEvent, leagueId, squadId, refreshKey }) => {
  const [seResult, setSeResult] = useState<any>(null);
  const [loadingResults, setLoadingResults] = useState(true);
  const [mysteryInput, setMysteryInput] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [editing, setEditing] = useState<{ gameNumber: number; field: string; value: string } | null>(null);
  const [payoutModal, setPayoutModal] = useState<{ bowlerId: number; bowlerName: string } | null>(null);
  const [doublesPayoutModal, setDoublesPayoutModal] = useState<{ bowlers: { bowler_id: number; bowler_name: string }[] } | null>(null);
  const [useHandicap, setUseHandicap] = useState(!!sideEvent.hdcp);
  const [matchingBowlers, setMatchingBowlers] = useState<any[]>([]);
  const [currentTarget, setCurrentTarget] = useState<number | null>(null);
  const [paidBowlerIds, setPaidBowlerIds] = useState<Set<number>>(new Set());
  const [awaPrintN, setAwaPrintN] = useState('10');

  const findMatches = useCallback(async (target: number) => {
    const isSeries = sideEvent.type === 'mystery_series';
    try {
      if (isSeries) {
        const r = await axios.post(
          `${API}/admin/manage-side-events/side-events/${sideEvent.id}/find-mystery-series`,
          { mysterySeries: target },
          { headers: authHeader() }
        );
        const results = r.data.results || [];
        setMatchingBowlers(results.map((b: any) => ({
          bowler_id: b.bowler_id,
          bowler_name: b.bowler_name,
          total_score: b.total_score,
        })));
      } else {
        const r = await axios.post(
          `${API}/admin/manage-side-events/side-events/${sideEvent.id}/find-mystery-score`,
          { mysteryScore: target },
          { headers: authHeader() }
        );
        const results = r.data.results || [];
        const byBowler: Record<number, { bowler_id: number; bowler_name: string; matching_games: { game: number; score: number }[] }> = {};
        for (const row of results) {
          if (!byBowler[row.bowler_id]) {
            byBowler[row.bowler_id] = { bowler_id: row.bowler_id, bowler_name: row.bowler_name, matching_games: [] };
          }
          byBowler[row.bowler_id].matching_games.push({ game: row.game_number, score: row.score });
        }
        setMatchingBowlers(Object.values(byBowler));
      }
      setCurrentTarget(target);
    } catch { setMatchingBowlers([]); }
  }, [sideEvent.id, sideEvent.type]);

  const loadData = useCallback(async () => {
    setLoadingResults(true);
    try {
      const hdcpParam = useHandicap ? '?useHandicap=true' : '';
      const [resultsRes, metaRes] = await Promise.all([
        axios.get(`${API}/admin/manage-side-events/squads/${squadId}/side-event-results${hdcpParam}`, { headers: authHeader() }),
        ['mystery_score', 'mystery_series'].includes(sideEvent.type)
          ? axios.get(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/custom-meta`, { headers: authHeader() })
          : Promise.resolve({ data: {} }),
      ]);
      const found = (resultsRes.data || []).find((r: any) => r.id === sideEvent.id) || null;
      setSeResult(found);
      const savedTarget = metaRes.data?.mystery_target;
      if (savedTarget !== undefined && savedTarget !== null) {
        const t = Number(savedTarget);
        setMysteryInput(String(t));
        findMatches(t);
      }
    } catch { } finally { setLoadingResults(false); }
  }, [sideEvent.id, sideEvent.type, squadId, useHandicap, findMatches]);

  const loadPaidBowlers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/payouts/side-event/${sideEvent.id}/paid-bowlers`, { headers: authHeader() });
      setPaidBowlerIds(new Set(r.data as number[]));
    } catch { /* non-critical */ }
  }, [sideEvent.id]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);
  useEffect(() => { loadPaidBowlers(); }, [loadPaidBowlers]);

  const saveMysteryTarget = async () => {
    const v = parseInt(mysteryInput, 10);
    if (isNaN(v)) return;
    setSavingMeta(true);
    try {
      await axios.put(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/custom-meta`, { mystery_target: v }, { headers: authHeader() });
      await findMatches(v);
    } catch { } finally { setSavingMeta(false); }
  };

  const rollRandom = () => {
    const min = sideEvent.type === 'mystery_series' ? 300 : 100;
    const max = sideEvent.type === 'mystery_series' ? 900 : 299;
    setMysteryInput(String(Math.floor(Math.random() * (max - min + 1)) + min));
  };

  const startEdit = (gameNumber: number, field: string, val: number) =>
    setEditing({ gameNumber, field, value: String(val) });

  const saveConfig = async () => {
    if (!editing) return;
    const num = parseFloat(editing.value);
    if (isNaN(num)) { setEditing(null); return; }
    try {
      const url = sideEvent.type === 'survivor'
        ? `${API}/admin/manage-side-events/side-events/${sideEvent.id}/survivor-config/${editing.gameNumber}`
        : `${API}/admin/manage-side-events/side-events/${sideEvent.id}/eliminator-config/${editing.gameNumber}`;
      await axios.put(url, { [editing.field]: num }, { headers: authHeader() });
      setEditing(null);
      loadData();
    } catch { setEditing(null); }
  };

  const printLiveRounds = () => {
    if (!seResult) return;
    const isSurvivor = sideEvent.type === 'survivor';
    const roundsHTML = (seResult.computed_rounds || []).map((r: any) => {
      const threshold = isSurvivor ? `Min Score: <strong>${r.survivor_score}</strong>` : `Advance Top: <strong>${r.advancement_percentage}%</strong>`;
      const cashers = isSurvivor ? (r.survivor_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') : (r.advancer_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ');
      const elim = (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ');
      return `<tr><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">${threshold}</td><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${cashers || '—'}</td><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim || '—'}</td></tr>`;
    }).join('');
    const finalCashers = (seResult.cashers || []).map((b: any) => b.bowler_name).join(', ') || 'None yet';
    const html = `<!DOCTYPE html><html><head><title>${sideEvent.name} – Live Rounds</title><style>body{font-family:sans-serif;padding:24px;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;}</style></head><body><h2 style="margin-bottom:2px;">${sideEvent.league_name}</h2><p style="color:#374151;font-size:13px;font-weight:600;margin:0 0 4px;">${sideEvent.name}</p><p style="color:#6b7280;font-size:13px;">Live ${isSurvivor ? 'Survivor' : 'Eliminator'} Rounds</p><table><thead><tr><th>Game</th><th>Threshold</th><th style="color:#15803d;">Survived / Advancing</th><th style="color:#b91c1c;">Eliminated</th></tr></thead><tbody>${roundsHTML}</tbody></table><div style="margin-top:20px;padding:12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;"><strong>Final Cashers:</strong> ${finalCashers}</div><p style="color:#9ca3af;font-size:11px;margin-top:20px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
    const w = window.open('', '_blank', 'width=820,height=600');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  const printAWA = () => {
    const n = Math.min(100, Math.max(2, parseInt(awaPrintN) || 10));
    const pots: any[] = seResult?.pot_results || [];
    if (pots.length === 0) { alert('No scores entered yet.'); return; }
    const potSections = pots.map((pot: any) => {
      const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
      const entries = (pot.all_entries || []).slice(0, n);
      if (entries.length === 0) return `<div style="margin-bottom:16px;"><h3 style="font-size:13px;margin:0 0 6px;">${label}</h3><em style="color:#9ca3af;">No scores entered</em></div>`;
      const rows = entries.map((b: any) => {
        const isPaid = pot.is_doubles
          ? (b.bowler_ids || []).some((bi: any) => paidBowlerIds.has(bi.bowler_id))
          : paidBowlerIds.has(b.bowler_id);
        const paidMark = isPaid ? ' <span style="display:inline-block;background:#fef3c7;color:#92400e;font-size:9px;font-weight:600;padding:1px 5px;border-radius:3px;margin-left:4px;">✓ PAID</span>' : '';
        return `<tr><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;">${b.rank}.</td><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;">${b.bowler_name}${paidMark}</td><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;font-weight:600;">${b.score}</td></tr>`;
      }).join('');
      return `<div style="margin-bottom:16px;"><h3 style="font-size:13px;margin:0 0 6px;">${label}</h3><table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr><th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">#</th><th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Name</th><th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Score</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }).join('<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;"/>');
    const html = `<!DOCTYPE html><html><head><title>${sideEvent.name} – Top ${n} Placements</title><style>body{font-family:sans-serif;padding:24px;color:#111;}</style></head><body><h2 style="margin-bottom:4px;">${sideEvent.name}</h2><p style="color:#6b7280;font-size:13px;margin-bottom:20px;">Placements 1 through ${n}</p>${potSections}<p style="color:#9ca3af;font-size:11px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
    const w = window.open('', '_blank', 'width=820,height=700');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  const toggleHandicap = () => {
    const next = !useHandicap;
    setUseHandicap(next);
    axios.patch(`${API}/admin/manage-side-events/side-events/${sideEvent.id}/hdcp`,
      { hdcp: next }, { headers: authHeader() }).catch(() => {});
  };

  if (loadingResults) return (
    <div className="mt-6 border-t border-gray-700 pt-4 flex justify-center">
      <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── SURVIVOR ──────────────────────────────────────────────────────────────
  if (sideEvent.type === 'survivor') {
    const rounds: any[] = seResult?.computed_rounds || seResult?.config?.map((c: any) => ({ game_number: c.game_number, survivor_score: c.survivor_score, round_payout: c.round_payout, survivor_list: [], eliminated_list: [], pending_list: [], survivors_count: 0, eliminated_count: 0 })) || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5 px-4 pb-4">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={leagueId}
          seResult={seResult}
          presetsApiBase={`${API}/admin/manage-side-events/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-emerald-300 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Live Survivor Rounds
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={toggleHandicap} className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
              Hdcp {useHandicap ? 'ON' : 'OFF'}
            </button>
            <button onClick={printLiveRounds} className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print Results
            </button>
          </div>
        </div>
        {rounds.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No rounds configured. Check your game numbers.</p>}
        {rounds.map((r: any) => (
          <div key={r.game_number} className="mb-3 bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">Min Score:</span>
                {editing?.gameNumber === r.game_number && editing?.field === 'survivor_score'
                  ? <input type="number" value={editing.value} autoFocus
                      onChange={e => setEditing(ex => ({ ...ex!, value: e.target.value }))}
                      onFocus={e => e.target.select()}
                      onBlur={saveConfig} onKeyDown={e => { if (e.key === 'Enter') saveConfig(); if (e.key === 'Escape') setEditing(null); }}
                      className="w-16 text-center bg-gray-600 border border-purple-500 rounded px-1.5 py-0.5 text-xs text-white focus:outline-none" />
                  : <button onClick={() => startEdit(r.game_number, 'survivor_score', r.survivor_score)}
                      className="text-xs font-bold text-purple-300 hover:text-yellow-300 bg-gray-600/60 px-2 py-0.5 rounded cursor-pointer transition-colors">
                      {r.survivor_score}
                    </button>
                }
                {r.round_payout > 0 && <span className="text-[10px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs mb-2">
              <span className="text-emerald-400">{r.survivors_count} survived</span>
              {r.eliminated_count > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
              {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
            </div>
            {r.survivor_list?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {[...r.survivor_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5 rounded">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
            {r.eliminated_list?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {[...r.eliminated_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-red-900/30 text-red-400/80 px-1.5 py-0.5 rounded line-through">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {seResult?.cashers?.length > 0 && (
          <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
            <div className="text-xs font-bold text-yellow-300 mb-2">🏆 Finalists</div>
            {[...seResult.cashers].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((c: any, i: number) => (
              <div key={c.bowler_id} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2 text-xs text-white">
                  <span className="text-gray-500">{i + 1}.</span>
                  <span className="font-medium">{c.bowler_name}</span>
                  {c.score != null && <span className="text-blue-300 text-[10px]">({c.score})</span>}
                  {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                </div>
                {paidBowlerIds.has(c.bowler_id) ? (
                  <span className="ml-3 px-2.5 py-1 bg-gray-700/80 text-green-400 text-[10px] rounded-lg font-semibold">✓ Paid – see Financials</span>
                ) : (
                  <button onClick={() => setPayoutModal({ bowlerId: c.bowler_id, bowlerName: c.bowler_name })}
                    className="ml-3 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded-lg font-semibold flex items-center gap-1 transition-colors">
                    💰 Pay Out
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {payoutModal && (
          <BookiePayoutModal isOpen={true} onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId} bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id} sideEventType="survivor" leagueId={leagueId}
            defaultAmount={0} onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }} />
        )}
      </div>
    );
  }

  // ── ELIMINATOR ────────────────────────────────────────────────────────────
  if (sideEvent.type === 'eliminator') {
    const rounds: any[] = seResult?.computed_rounds || seResult?.config?.map((c: any) => ({ game_number: c.game_number, advancement_percentage: c.advancement_percentage, round_payout: c.round_payout, advancer_list: [], eliminated_list: [], pending_list: [], advancers_count: 0, eliminated_count: 0 })) || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5 px-4 pb-4">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={leagueId}
          seResult={seResult}
          presetsApiBase={`${API}/admin/manage-side-events/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-orange-300 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-orange-400" />
            Live Eliminator Rounds
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={toggleHandicap} className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
              Hdcp {useHandicap ? 'ON' : 'OFF'}
            </button>
            <button onClick={printLiveRounds} className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print Results
            </button>
          </div>
        </div>
        {rounds.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No rounds configured. Check your game numbers.</p>}
        {rounds.map((r: any) => (
          <div key={r.game_number} className="mb-3 bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">Advance:</span>
                {editing?.gameNumber === r.game_number && editing?.field === 'advancement_percentage'
                  ? <select value={editing.value} autoFocus
                      onChange={e => { setEditing(ex => ({ ...ex!, value: e.target.value })); }}
                      onBlur={saveConfig}
                      style={{ colorScheme: 'dark' }}
                      className="bg-gray-600 border border-purple-500 rounded px-1.5 py-0.5 text-xs text-white focus:outline-none">
                      <option value={25} className="bg-gray-800">1 of 4 (25%)</option>
                      <option value={33} className="bg-gray-800">1 of 3 (33%)</option>
                      <option value={50} className="bg-gray-800">1 of 2 (50%)</option>
                      <option value={67} className="bg-gray-800">2 of 3 (67%)</option>
                      <option value={75} className="bg-gray-800">3 of 4 (75%)</option>
                      <option value={85} className="bg-gray-800">Top 85% (85%)</option>
                      <option value={100} className="bg-gray-800">All (100%)</option>
                    </select>
                  : <button onClick={() => startEdit(r.game_number, 'advancement_percentage', r.advancement_percentage)}
                      className="text-xs font-bold text-purple-300 hover:text-yellow-300 bg-gray-600/60 px-2 py-0.5 rounded cursor-pointer transition-colors">
                      {r.advancement_percentage}%
                    </button>
                }
                {r.cutoff_score && <span className="text-[10px] text-gray-400">cutoff: {r.cutoff_score}</span>}
                {r.round_payout > 0 && <span className="text-[10px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs mb-2">
              <span className="text-orange-400">{r.advancers_count ?? 0} advancing</span>
              {(r.eliminated_count ?? 0) > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
              {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
            </div>
            {r.advancer_list?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {[...r.advancer_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-orange-900/40 text-orange-300 px-1.5 py-0.5 rounded">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
            {r.eliminated_list?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {[...r.eliminated_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-red-900/30 text-red-400/80 px-1.5 py-0.5 rounded line-through">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {seResult?.cashers?.length > 0 && (
          <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
            <div className="text-xs font-bold text-yellow-300 mb-2">🏆 Finalists</div>
            {[...seResult.cashers].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((c: any, i: number) => (
              <div key={c.bowler_id} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2 text-xs text-white">
                  <span className="text-gray-500">{i + 1}.</span>
                  <span className="font-medium">{c.bowler_name}</span>
                  {c.score != null && <span className="text-blue-300 text-[10px]">({c.score})</span>}
                  {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                </div>
                {paidBowlerIds.has(c.bowler_id) ? (
                  <span className="ml-3 px-2.5 py-1 bg-gray-700/80 text-green-400 text-[10px] rounded-lg font-semibold">✓ Paid – see Financials</span>
                ) : (
                  <button onClick={() => setPayoutModal({ bowlerId: c.bowler_id, bowlerName: c.bowler_name })}
                    className="ml-3 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded-lg font-semibold flex items-center gap-1 transition-colors">
                    💰 Pay Out
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {payoutModal && (
          <BookiePayoutModal isOpen={true} onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId} bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id} sideEventType="eliminator" leagueId={leagueId}
            defaultAmount={0} onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }} />
        )}
      </div>
    );
  }

  // ── MYSTERY SCORE / MYSTERY SERIES ────────────────────────────────────────
  if (sideEvent.type === 'mystery_score' || sideEvent.type === 'mystery_series') {
    const isSeries = sideEvent.type === 'mystery_series';
    return (
      <div className="mt-6 border-t border-gray-700 pt-5 px-4 pb-4">
        <h4 className="text-sm font-bold text-blue-300 mb-3">
          {isSeries ? 'Mystery Series' : 'Mystery Score'} Target
        </h4>
        <div className="flex items-center gap-2 mb-4">
          <input type="number" value={mysteryInput} onChange={e => setMysteryInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveMysteryTarget()}
            placeholder={isSeries ? 'e.g. 550' : 'e.g. 200'}
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500" />
          <button onClick={rollRandom} className="px-3 py-2 bg-indigo-700/70 hover:bg-indigo-700 text-white text-xs rounded-lg font-medium">
            🎲 Random
          </button>
          <button onClick={saveMysteryTarget} disabled={savingMeta} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium disabled:opacity-50">
            {savingMeta ? '...' : 'Set'}
          </button>
        </div>
        {currentTarget !== null && (
          <p className="text-xs text-blue-400 mb-3">
            Target: <span className="font-bold text-white text-sm">{currentTarget}</span>{isSeries ? ' (series total)' : ''}
          </p>
        )}
        {matchingBowlers.length === 0
          ? <div className="text-center text-gray-500 py-6 text-xs">
              {currentTarget !== null ? 'No bowlers match the target yet — enter scores and hit Set again.' : 'Set a target to check for matches.'}
            </div>
          : <div className="space-y-2">
              <p className="text-xs text-green-400 font-semibold mb-2">{matchingBowlers.length} match{matchingBowlers.length > 1 ? 'es' : ''} found!</p>
              {matchingBowlers.map((b: any) => (
                <div key={b.bowler_id} className="flex items-center justify-between bg-green-900/30 border border-green-500/40 rounded-lg px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-white">{b.bowler_name}</div>
                    <div className="text-green-400 text-xs font-bold">
                      {isSeries ? `${b.total_score} series ✓` : `${(b.matching_games || []).map((g: any) => `G${g.game}: ${g.score}`).join(', ')} ✓`}
                    </div>
                  </div>
                  <button onClick={() => setPayoutModal({ bowlerId: b.bowler_id, bowlerName: b.bowler_name })}
                    className="ml-3 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg font-semibold flex items-center gap-1.5 transition-colors">
                    💰 Pay Out
                  </button>
                </div>
              ))}
            </div>
        }
        {payoutModal && (
          <BookiePayoutModal isOpen={true} onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId} bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id} sideEventType={sideEvent.type as 'mystery_score' | 'mystery_series'} leagueId={leagueId}
            defaultAmount={0} onPayoutComplete={() => setPayoutModal(null)} />
        )}
      </div>
    );
  }

  // ── SIDE POTS / ALL WITH ALL ──────────────────────────────────────────────
  if (sideEvent.type === 'side_pots' || sideEvent.type === 'custom') {
    const pots: any[] = seResult?.pot_results || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5 px-4 pb-4">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={leagueId}
          seResult={seResult}
          presetsApiBase={`${API}/admin/manage-side-events/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-amber-300">Side Pot Results</h4>
          <button onClick={toggleHandicap} className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
            Hdcp {useHandicap ? 'ON' : 'OFF'}
          </button>
        </div>
        {sideEvent.type === 'custom' && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-gray-700/40 rounded-lg">
            <span className="text-xs text-gray-300">Print placements 1 through</span>
            <input
              type="number" min={2} max={100} value={awaPrintN}
              onChange={e => setAwaPrintN(e.target.value)}
              className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white text-center focus:outline-none focus:border-blue-500" />
            <span className="text-[10px] text-gray-500">(min 2 · max 100)</span>
            <button onClick={printAWA}
              className="ml-1 px-2.5 py-1 bg-blue-700/80 hover:bg-blue-700 text-white text-xs rounded-lg flex items-center gap-1 transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print
            </button>
          </div>
        )}
        {pots.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No side pot configurations found.</p>}
        {pots.map((pot: any, pi: number) => (
          <div key={pi} className="mb-4 bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
            <div className="px-3 py-2 bg-gray-700/50 border-b border-gray-600/50 flex items-center justify-between">
              <span className="text-xs font-semibold text-white">
                {pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`}
              </span>
              <span className="text-[10px] text-gray-400">Top {pot.winners_pct}% → {pot.winner_count} winner{pot.winner_count !== 1 ? 's' : ''}</span>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {(!pot.all_entries || pot.all_entries.length === 0)
                ? <div className="px-3 py-3 text-xs text-gray-500 text-center">No scores entered yet</div>
                : (() => {
                    const cutoffScore = pot.all_entries[pot.winner_count - 1]?.score ?? null;
                    return pot.all_entries.map((b: any) => {
                      const isWinner = b.rank <= pot.winner_count;
                      const isTied = !isWinner && cutoffScore !== null && b.score === cutoffScore;
                      return (
                        <div key={b.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                          <div className="flex items-center gap-1.5">
                            <span>{b.rank}. {b.bowler_name}</span>
                            {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full font-semibold">TIE</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>{b.score}</span>
                            {(isWinner || isTied) && !pot.is_doubles && (
                              paidBowlerIds.has(b.bowler_id)
                                ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                                : <button onClick={() => setPayoutModal({ bowlerId: b.bowler_id, bowlerName: b.bowler_name })}
                                    className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors ${isWinner ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>
                                    💰 Pay Out
                                  </button>
                            )}
                            {(isWinner || isTied) && pot.is_doubles && (b.bowler_ids?.length ?? 0) > 0 && (
                              (b.bowler_ids || []).some((bi: any) => paidBowlerIds.has(bi.bowler_id))
                                ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                                : <button onClick={() => setDoublesPayoutModal({ bowlers: b.bowler_ids })}
                                    className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors ${isWinner ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>
                                    💰 Pay Pair
                                  </button>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()
              }
            </div>
          </div>
        ))}
        {payoutModal && (
          <BookiePayoutModal isOpen={true} onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId} bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id} sideEventType="side_pots" leagueId={leagueId}
            defaultAmount={0} onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }} />
        )}
        {doublesPayoutModal && (
          <DoublesPayoutModal bowlers={doublesPayoutModal.bowlers} sideEventId={sideEvent.id} leagueId={leagueId}
            onClose={() => setDoublesPayoutModal(null)} onComplete={() => { setDoublesPayoutModal(null); loadPaidBowlers(); }} />
        )}
      </div>
    );
  }

  // ── HIGH GAME / HIGH SERIES ───────────────────────────────────────────────
  if (sideEvent.type === 'high_game' || sideEvent.type === 'high_series') {
    const isHighSeries = sideEvent.type === 'high_series';
    const standings: any[] = seResult?.standings || [];
    const winnerScore = standings.length > 0 ? (isHighSeries ? standings[0].total_score : standings[0].best_game) : null;
    return (
      <div className="mt-6 border-t border-gray-700 pt-5 px-4 pb-4">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={leagueId}
          seResult={seResult}
          presetsApiBase={`${API}/admin/manage-side-events/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-amber-300">
            {isHighSeries ? 'High Series' : 'High Game'} Leaderboard
          </h4>
          <button onClick={toggleHandicap} className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
            Hdcp {useHandicap ? 'ON' : 'OFF'}
          </button>
        </div>
        {standings.length === 0
          ? <p className="text-xs text-gray-500 text-center py-4">No scores entered yet</p>
          : <div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
              {standings.map((s: any, i: number) => {
                const displayScore = isHighSeries ? s.total_score : s.best_game;
                const isWinner = i === 0;
                const isTied = !isWinner && winnerScore !== null && displayScore === winnerScore;
                return (
                  <div key={s.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                    <div className="flex items-center gap-1.5">
                      <span>{i + 1}. {s.bowler_name}</span>
                      {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full font-semibold">TIE</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>
                        {displayScore ?? '—'}{isHighSeries ? ' ser' : ''}
                      </span>
                      {(isWinner || isTied) && (
                        paidBowlerIds.has(s.bowler_id)
                          ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                          : <button onClick={() => setPayoutModal({ bowlerId: s.bowler_id, bowlerName: s.bowler_name })}
                              className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors ${isWinner ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>
                              💰 Pay Out
                            </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        }
        {payoutModal && (
          <BookiePayoutModal isOpen={true} onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId} bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id} sideEventType={sideEvent.type as 'high_game' | 'high_series'} leagueId={leagueId}
            defaultAmount={0} onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }} />
        )}
      </div>
    );
  }

  return null;
};

/* ─── Main Component ────────────────────────────────────────────────────── */
const AdminSideActionDashboard: React.FC<Props> = ({ leagueId, onBack, onNavigateToSchedule }) => {
  const [squads, setSquads] = useState<Squad[]>([]);
  const [selectedSquadId, setSelectedSquadId] = useState<number | null>(null);
  const [squadSEs, setSquadSEs] = useState<AdminSideEvent[]>([]);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const [loadingSEs, setLoadingSEs] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [leagueName, setLeagueName] = useState('');
  const [selectedSEId, setSelectedSEId] = useState<number | null>(null);
  const [showAddSE, setShowAddSE] = useState(false);
  const [addSESquadId, setAddSESquadId] = useState<number | null>(null);
  const [showFinancials, setShowFinancials] = useState(false);
  const [financialsSquadId, setFinancialsSquadId] = useState<number | null>(null);
  // ── Helper: open quick-entry windows in a new popup (split-screen friendly) ──
  const openQuickEntriesWindow = () => {
    if (!selectedSquadId || !leagueId) return;
    window.open(
      `/admin/quick-entries/${leagueId}/${selectedSquadId}`,
      `admin-quick-entries-${leagueId}-${selectedSquadId}`,
      'width=1280,height=860,top=80,left=80'
    );
  };

  const openQuickBracketEntriesWindow = () => {
    if (!selectedSquadId || !leagueId) return;
    window.open(
      `/admin/quick-bracket-entries/${leagueId}/${selectedSquadId}`,
      `admin-quick-bracket-entries-${leagueId}-${selectedSquadId}`,
      'width=1280,height=860,top=80,left=800'
    );
  };
  const [showAddSquad, setShowAddSquad] = useState(false);

  const [deletingSquadId, setDeletingSquadId] = useState<number | null>(null);
  const [deletingSEId, setDeletingSEId] = useState<number | null>(null);

  const currentSquad = squads.find(s => s.id === selectedSquadId);
  const selectedSE = squadSEs.find(se => se.id === selectedSEId) ?? null;

  /* ── Data loading ─────────────────────────────────────────────────────── */
  const fetchSquads = useCallback(async () => {
    setLoadingSquads(true);
    try {
      const res = await fetch(`${API}/admin/leagues/${leagueId}/management`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        const fetchedSquads: Squad[] = data.squads || [];
        setSquads(fetchedSquads);
        if (data.league_name) setLeagueName(data.league_name);
        setSelectedSquadId(prev => prev ?? (fetchedSquads[0]?.id ?? null));
      }
    } catch (e) { console.error(e); } finally { setLoadingSquads(false); }
  }, [leagueId]);

  const fetchSquadSEs = useCallback(async () => {
    if (!selectedSquadId) return;
    setLoadingSEs(true);
    try {
      const res = await fetch(`${API}/admin/leagues/${leagueId}/side-events/by-squad?squadId=${selectedSquadId}`, { headers: authHeader() });
      if (res.ok) {
        const list: AdminSideEvent[] = await res.json();
        setSquadSEs(list);
        setSelectedSEId(prev => (list.find(se => se.id === prev) ? prev : (list[0]?.id ?? null)));
      }
    } catch (e) { console.error(e); } finally { setLoadingSEs(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, selectedSquadId, refreshKey]);

  useEffect(() => { fetchSquads(); }, [fetchSquads]);
  useEffect(() => { fetchSquadSEs(); }, [fetchSquadSEs]);

  const handleRefresh = () => setRefreshKey(k => k + 1);

  // ── Listen for refresh broadcasts from popup quick-entry windows ───────────
  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel('admin-entries-refresh');
      ch.onmessage = () => handleRefresh();
    } catch { /* BroadcastChannel not available */ }
    return () => { ch?.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Delete squad ─────────────────────────────────────────────────────── */
  const deleteSquad = async (squadId: number) => {
    if (!window.confirm('Delete this squad and all its side events?')) return;
    try {
      await axios.delete(`${API}/admin/manage-side-events/squads/${squadId}`, { headers: authHeader() });
      if (selectedSquadId === squadId) setSelectedSquadId(null);
      await fetchSquads();
    } catch (e) { console.error(e); } finally { setDeletingSquadId(null); }
  };

  /* ── Delete side event ────────────────────────────────────────────────── */
  const deleteSE = async (seId: number) => {
    if (!window.confirm('Delete this side event and all its entries?')) return;
    try {
      await axios.delete(`${API}/admin/manage-side-events/side-events/${seId}`, { headers: authHeader() });
      if (selectedSEId === seId) setSelectedSEId(null);
      handleRefresh();
    } catch (e) { console.error(e); } finally { setDeletingSEId(null); }
  };

  /* ── Print results ────────────────────────────────────────────────────── */
  const printSquadResults = async (sqId: number, sqName: string) => {
    try {
      const [r, payRes] = await Promise.all([
        fetch(`${API}/admin/manage-side-events/squads/${sqId}/side-event-results`, { headers: authHeader() }),
        fetch(`${API}/admin/manage-side-events/squads/${sqId}/payouts`, { headers: authHeader() }),
      ]);
      if (!r.ok) return;
      const results = await r.json();
      const payouts = payRes.ok ? (await payRes.json()).payouts || [] : [];
      const paidMap = new Map<number, Set<number>>();
      for (const p of (payouts as { side_event_id: number; recipient_id: number }[])) {
        if (!paidMap.has(p.side_event_id)) paidMap.set(p.side_event_id, new Set());
        paidMap.get(p.side_event_id)!.add(p.recipient_id);
      }
      printWinners(leagueName, sqName, results, paidMap);
    } catch (e) { console.error(e); }
  };

  const printAllResults = async () => {
    const allResults: any[] = [];
    for (const sq of squads) {
      try {
        const r = await fetch(`${API}/admin/manage-side-events/squads/${sq.id}/side-event-results`, { headers: authHeader() });
        if (r.ok) allResults.push(...(await r.json()));
      } catch (_) {}
    }
    try {
      const payRes = await fetch(`${API}/admin/manage-side-events/leagues/${leagueId}/payouts`, { headers: authHeader() });
      const payouts = payRes.ok ? (await payRes.json()).payouts || [] : [];
      const paidMap = new Map<number, Set<number>>();
      for (const p of (payouts as { side_event_id: number; recipient_id: number }[])) {
        if (!paidMap.has(p.side_event_id)) paidMap.set(p.side_event_id, new Set());
        paidMap.get(p.side_event_id)!.add(p.recipient_id);
      }
      printWinners(leagueName, 'All Squads', allResults, paidMap);
    } catch (_) {
      printWinners(leagueName, 'All Squads', allResults);
    }
  };

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div className="h-screen bg-gray-900 flex flex-col text-white overflow-hidden">

      {/* ── Header ── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors flex-shrink-0" title="Back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-white leading-none">Side Action Dashboard</h1>
            {leagueName && <p className="text-xs text-gray-400 mt-0.5">{leagueName}</p>}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => { setAddSESquadId(null); setShowAddSE(true); }} className="px-2.5 py-1.5 bg-purple-700/80 hover:bg-purple-700 text-xs text-white rounded-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add SE (All)
            </button>
            <button onClick={() => { setFinancialsSquadId(null); setShowFinancials(true); }} className="px-2.5 py-1.5 bg-green-700 hover:bg-green-600 text-xs text-white rounded-lg">
               Financials (All)
            </button>
            <button onClick={printAllResults} className="px-2.5 py-1.5 bg-blue-700 hover:bg-blue-600 text-xs text-white rounded-lg">
              🖨 Print All Results
            </button>
            
           
          </div>
        </div>
      </div>

      {/* ── Squad Tabs ── */}
      <div className="bg-gray-800 border-b border-gray-700 flex-shrink-0">
        {loadingSquads ? (
          <div className="py-3 px-4 text-xs text-gray-500">Loading squads…</div>
        ) : squads.length === 0 ? (
          <div className="py-3 px-4 text-xs text-gray-500">No squads. Create squads in the Schedule section.</div>
        ) : (
          <div className="flex items-end overflow-x-auto px-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.12) transparent' }}>
            {squads.map(sq => (
              <div key={sq.id} className="flex-shrink-0 relative group">
                <button
                  onClick={() => { setSelectedSquadId(sq.id); setSelectedSEId(null); }}
                  className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 mr-1 whitespace-nowrap
                    ${selectedSquadId === sq.id ? 'border-purple-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                >
                  <span>{sq.name}</span>
                  <span className="ml-1 text-gray-500">{fmtDate(sq.date)}</span>
                </button>
                <button
                  onClick={e => { e.stopPropagation(); deleteSquad(sq.id); }}
                  disabled={deletingSquadId === sq.id}
                  className="absolute top-1 right-0 opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 text-xs transition-opacity disabled:opacity-30"
                  title="Delete squad"
                >×</button>
              </div>
            ))}
            <button onClick={() => setShowAddSquad(true)} className="flex-shrink-0 px-2 py-2 text-xs text-gray-500 hover:text-purple-400 border-b-2 border-transparent whitespace-nowrap mb-0.5">
              + Squad
            </button>
          </div>
        )}
      </div>

      {/* ── Per-Squad Section ── */}
      {selectedSquadId && (
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Per-squad action bar */}
          <div className="bg-gray-800/60 border-b border-gray-700/60 px-4 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0">
            <button onClick={() => { setAddSESquadId(selectedSquadId); setShowAddSE(true); }} className="px-2.5 py-1 bg-purple-700/70 hover:bg-purple-700 text-xs text-white rounded-md flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add SE (This Week)
            </button>
            <button onClick={() => { setFinancialsSquadId(selectedSquadId); setShowFinancials(true); }} className="px-2.5 py-1 bg-green-700 hover:bg-green-600 text-xs text-white rounded-md">
               Financials
            </button>
            {onNavigateToSchedule && (
              <button onClick={onNavigateToSchedule} className="px-2.5 py-1 bg-pink-700 hover:bg-pink-600 text-xs text-white rounded-md">
                📋 Enter Scores
              </button>
            )}
            <button onClick={openQuickEntriesWindow} disabled={!squadSEs.some(se => se.type !== 'bracket')} className="px-2.5 py-1 bg-teal-700/70 hover:bg-teal-700 text-xs text-white rounded-md disabled:opacity-40">
              Quick Entries
            </button>
            <button onClick={openQuickBracketEntriesWindow} disabled={!squadSEs.some(se => se.type === 'bracket')} className="px-2.5 py-1 bg-indigo-700/70 hover:bg-indigo-700 text-xs text-white rounded-md disabled:opacity-40">
              Quick Bracket Entries
            </button>
            <button onClick={() => currentSquad && printSquadResults(selectedSquadId, currentSquad.name)} className="px-2.5 py-1 bg-blue-700 hover:bg-blue-600 text-xs text-white rounded-md">
              🖨 Print Results
            </button>
          </div>

          {/* SE Tabs */}
          {loadingSEs ? (
            <div className="p-4 text-xs text-gray-500">Loading side events…</div>
          ) : squadSEs.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 gap-3">
              <p className="text-gray-500 text-sm">No side events for this squad.</p>
              <button onClick={() => { setAddSESquadId(selectedSquadId); setShowAddSE(true); }} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-sm text-white rounded-lg">
                Add First Side Event
              </button>
            </div>
          ) : (
            <>
              {/* SE tab bar */}
              <div className="bg-gray-800/40 border-b border-gray-700/60 flex-shrink-0 overflow-x-auto">
                <div className="flex items-end px-4 pt-1" style={{ scrollbarWidth: 'none' }}>
                  {squadSEs.map(se => (
                    <div key={se.id} className="flex-shrink-0 relative group mr-0.5">
                      <button
                        onClick={() => setSelectedSEId(se.id)}
                        className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap
                          ${selectedSEId === se.id ? 'border-purple-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                      >
                        <span className={`px-1.5 py-0.5 rounded text-xs ${TYPE_COLORS[se.type] || 'bg-gray-700 text-gray-300'}`}>{TYPE_LABELS[se.type] || se.type}</span>
                        <span>{se.name}</span>
                        {Number(se.entry_count) > 0 && (
                          <span className="bg-blue-800 text-blue-200 text-xs px-1.5 rounded-full">{se.type === 'custom' ? Math.round(Math.sqrt(se.entry_count)) : se.entry_count}</span>
                        )}
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); deleteSE(se.id); }}
                        disabled={deletingSEId === se.id}
                        className="absolute top-1.5 right-0 opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 text-xs transition-opacity"
                        title="Delete side event"
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SE content panel */}
              <div className="flex-1 overflow-y-auto">
                {selectedSE ? (
                  <>
                    {/* Panel header */}
                    <div className="px-4 pt-3 pb-0 flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS[selectedSE.type] || 'bg-gray-700 text-gray-300'}`}>{TYPE_LABELS[selectedSE.type] || selectedSE.type}</span>
                      <span className="text-sm font-semibold text-white">{selectedSE.name}</span>
                      {Number(selectedSE.cost) > 0 && <span className="text-xs text-green-400">${Number(selectedSE.cost).toFixed(2)}/entry</span>}
                      {selectedSE.applies_to && <span className="text-xs text-gray-500 capitalize">{selectedSE.applies_to}</span>}
                    </div>

                    {/* Entries panel */}
                    {selectedSE.type === 'bracket' ? (
                      <BracketEntriesPanel sideEvent={selectedSE} leagueId={leagueId} onRefresh={handleRefresh} />
                    ) : selectedSE.type === 'clean_game' ? (
                      <CleanGamePanel sideEvent={selectedSE} leagueId={leagueId} onRefresh={handleRefresh} />
                    ) : selectedSE.applies_to === 'doubles' ? (
                      <AdminDoublesPanel sideEvent={selectedSE} leagueId={leagueId} onRefresh={handleRefresh} />
                    ) : (
                      <EntriesPanel sideEvent={selectedSE} leagueId={leagueId} onRefresh={handleRefresh} />
                    )}

                    {/* Live results panel for live-types */}
                    {LIVE_TYPES.has(selectedSE.type) && selectedSquadId && (
                      <SELivePanel
                        sideEvent={selectedSE}
                        leagueId={leagueId}
                        squadId={selectedSquadId}
                        refreshKey={refreshKey}
                      />
                    )}
                  </>
                ) : (
                  <div className="p-4 text-xs text-gray-500">Select a side event above.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showAddSE && (
        <AddSideEvent
          leagueId={leagueId}
          defaultSquadId={addSESquadId}
          onClose={() => setShowAddSE(false)}
          onSideEventAdded={() => { setShowAddSE(false); handleRefresh(); }}
        />
      )}

      {showFinancials && (
        <FinancialsPanel
          leagueId={leagueId}
          squadId={financialsSquadId}
          leagueName={leagueName}
          squadName={squads.find(s => s.id === financialsSquadId)?.name}
          onClose={() => setShowFinancials(false)}
        />
      )}

      {showAddSquad && (
        <AddSquadModal
          leagueId={leagueId}
          onClose={() => setShowAddSquad(false)}
          onAdded={() => { setShowAddSquad(false); fetchSquads(); }}
        />
      )}

    
    </div>
  );
};

export default AdminSideActionDashboard;
